/* Cake customiser web component. Vanilla, independent of Horizon's
   @theme/component import map because tfk-product.liquid is the bespoke,
   self-contained PDP. Contract: tfk-contracts/cake-customiser/storefront-api.md */
(function () {
  "use strict";

  const API_BASE = "/apps/customise";
  /* The storefront contract this theme implements, sent on the completion
     page's save from v3.4 (C70) so the app can tell a theme that sends
     `change` from an older one that can't. Raise it with the contract. */
  const THEME_CONTRACT = "3.6";
  /* Contract v3.6 (C79): the family the app's symbol font is loaded under, the
     font the print draws ★ ✓ ✿ and other symbols with. */
  const SYMBOL_FAMILY = "tfk-customiser-symbols";
  /* Display zoom for the mockup in the preview (2026-07-15 app team request).
     Conforming mockups carry a wide background border (canonical framing pins
     the sheet at 52% of photo width), so the contain-fit rect is scaled up,
     still centred, cropping only border. 1.15 is the no-crop maximum: the XL
     cake (87% of photo width under its 44% area override) lands at about
     100% of the preview. Raising this crops the XL cake's sides. */
  const MOCKUP_DISPLAY_ZOOM = 1.15;

  /* The smallest Celebration text size a customer can choose (contract v3.3,
     ruled by Joachim 29 September 2026): the size under which the page already
     warns that a message may look small. The app rejects anything smaller. */
  const MIN_TEXT_SIZE_MM = 10;

  /* Slab photo crop geometry (contract v3.2 contain fit). Pure: source pixels
     and photo-area px in, numbers out. No DOM, no component state, no canvas.

     It lives out here so the theme's own test suite can call the SAME code the
     preview calls, and so the parity vectors the app's render test consumes are
     produced by this function rather than by a second copy of the rule written
     down twice. The preview and the print file are separate implementations
     that agree only by contract; a shared crop rule is the only part of that
     agreement a test can actually hold on to.

     The cupcake disc path (slotCropDims/drawSlot) is deliberately NOT routed
     through here. It is already contain-on-white under v2.7, on a square
     target, with its own three-anchor zoom. Merging the two would change disc
     framing, which this work must not touch. */
  const TfkCakeGeometry = {
    /* Crop dims at zoom 1: the largest rect carrying the photo area's aspect
       that fits inside the image. Unchanged from the pre-v3.2 rule, character
       for character, because zoom 1 is the default and must not move. */
    coverDims(srcW, srcH, areaW, areaH) {
      const areaAspect = areaW / areaH;
      const srcAspect = srcW / srcH;
      return srcAspect > areaAspect
        ? { cropW: srcH * areaAspect, cropH: srcH }
        : { cropW: srcW, cropH: srcW / areaAspect };
    },

    /* The zoom at which the WHOLE image is framed, which is where the slider's
       left end sits (ruling 2026-09-16: computed for this design and this
       upload, never a fixed percentage).

       It is the cover crop's length on the axis cover TRIMMED, over the image's
       full length on that axis, which reduces to min(A/S, S/A) for area aspect
       A and source aspect S. It is 1 when the two aspects already match, so a
       photo shaped exactly like the print area has nothing to zoom out to and
       the slider keeps its current range. */
    minZoom(srcW, srcH, areaW, areaH) {
      const a = areaW / areaH;
      const s = srcW / srcH;
      return Math.min(a / s, s / a);
    },

    /* Crop dims at any zoom.

       At zoom >= 1 both axes shrink by the zoom, exactly as before v3.2.

       Below 1 only the axis cover trimmed grows, up to the full source
       dimension. The other axis is ALREADY the whole image: scaling it too
       (what `cropW /= zoom; cropH /= zoom` would do) walks the crop off the
       edge of the picture, and sharp.extract rejects an out-of-bounds region
       rather than padding it. This is the same two-phase shape as the cupcake
       path's slotCropDims. */
    cropDims(srcW, srcH, areaW, areaH, zoom) {
      const cover = this.coverDims(srcW, srcH, areaW, areaH);
      if (zoom >= 1) return { cropW: cover.cropW / zoom, cropH: cover.cropH / zoom };
      return srcW / srcH > areaW / areaH
        ? { cropW: Math.min(srcW, cover.cropW / zoom), cropH: cover.cropH }
        : { cropW: cover.cropW, cropH: Math.min(srcH, cover.cropH / zoom) };
    },

    /* Integer crop rect in EXIF-upright source pixels, always in-bounds:
       x, y >= 0 and the rect inside the image, which is what CropSchema and
       sharp.extract both require. `pan` is the crop centre as a fraction of
       the source. Rounding is unchanged from the pre-v3.2 computeCrop. */
    cropRect(srcW, srcH, areaW, areaH, zoom, pan) {
      const { cropW, cropH } = this.cropDims(srcW, srcH, areaW, areaH, zoom);
      const x = Math.round(pan.x * srcW - cropW / 2);
      const y = Math.round(pan.y * srcH - cropH / 2);
      return {
        x: Math.max(0, Math.min(srcW - Math.round(cropW), x)),
        y: Math.max(0, Math.min(srcH - Math.round(cropH), y)),
        width: Math.round(cropW),
        height: Math.round(cropH),
      };
    },

    /* The fit the server must apply to this crop (contract v3.2).

       Below the fill point the crop no longer carries the photo area's aspect,
       so a cover resize would crop away exactly what the customer zoomed out to
       see. At or above it, cover and contain agree, and the theme omits the
       field so the payload stays byte-identical to what it sent before v3.2. */
    fitFor(zoom) {
      return zoom < 1 ? "contain" : "cover";
    },

    /* The pan range is [h, 1 - h] on each axis, h being half the crop as a
       fraction of the source. Lifted verbatim from the component's clampPan so
       the zoom tests exercise the real rule. */
    clampPan(srcW, srcH, areaW, areaH, zoom, pan) {
      const crop = this.cropRect(srcW, srcH, areaW, areaH, zoom, pan);
      const hx = crop.width / 2 / srcW;
      const hy = crop.height / 2 / srcH;
      return {
        x: Math.min(1 - hx, Math.max(hx, pan.x)),
        y: Math.min(1 - hy, Math.max(hy, pan.y)),
      };
    },

    /* The pan to carry across a slider move (C26 fix 3, ruled 2026-09-16).

       Zooming IN keeps the point under the preview's centre, as before: the
       range only widens, so nothing clamps.

       Zooming OUT narrows the range, and keeping the centre point then let
       clampPan deposit a deliberate zoom-2 framing exactly on the zoom-1
       clamp, one edge hard against the print area, without the customer
       touching the image. So on zoom out the pan keeps its position as a
       fraction of the range instead: 70% of the way to the left edge stays
       70% of the way. A framing already ON the clamp stays on it, which is
       the customer's own edge framing and which cutEdges still reports. */
    panForZoom(srcW, srcH, areaW, areaH, fromZoom, toZoom, pan) {
      if (!(toZoom < fromZoom)) return { x: pan.x, y: pan.y };
      const before = this.cropRect(srcW, srcH, areaW, areaH, fromZoom, { x: 0.5, y: 0.5 });
      const after = this.cropRect(srcW, srcH, areaW, areaH, toZoom, { x: 0.5, y: 0.5 });
      const carry = (p, cropBefore, cropAfter, src) => {
        const rangeBefore = 0.5 - cropBefore / 2 / src;
        const rangeAfter = 0.5 - cropAfter / 2 / src;
        if (rangeBefore <= 0) return p;
        const t = Math.min(1, Math.max(-1, (p - 0.5) / rangeBefore));
        return 0.5 + t * Math.max(0, rangeAfter);
      };
      return {
        x: carry(pan.x, before.width, after.width, srcW),
        y: carry(pan.y, before.height, after.height, srcH),
      };
    },

    /* The edges of the photo the customer's own framing has pushed outside the
       print area (C26 fix 1). Order: top, right, bottom, left.

       NOT "every edge that loses anything". At the default framing (zoom 1,
       pan centred) the fill fit already trims the slack axis evenly, so almost
       every untouched upload loses something, and a warning on that would be
       permanently on. An edge is reported only when it loses MORE than it
       loses at the default framing, by more than 1% of that dimension, which
       absorbs the default's own rounding (40 px one side, 39 the other).

       Silence at the default rests on the zoom help text under the slider
       ("Drag left to fit your whole photo on the cake...") being the standing
       notice that the fill fit trims. If that line is removed or reworded so
       it no longer tells the customer the photo is cut, revisit this trigger:
       silence at the default would then be real silence. */
    cutEdges(srcW, srcH, areaW, areaH, zoom, pan) {
      const crop = this.cropRect(srcW, srcH, areaW, areaH, zoom, pan);
      const base = this.cropRect(srcW, srcH, areaW, areaH, 1, { x: 0.5, y: 0.5 });
      const loss = (r) => ({
        top: r.y,
        right: srcW - r.x - r.width,
        bottom: srcH - r.y - r.height,
        left: r.x,
      });
      const now = loss(crop);
      const was = loss(base);
      const tol = { top: srcH * 0.01, right: srcW * 0.01, bottom: srcH * 0.01, left: srcW * 0.01 };
      return ["top", "right", "bottom", "left"].filter((e) => now[e] > was[e] + tol[e]);
    },

    /* Customer-facing copy for cutEdges. Edge-naming wording, recommended to
       Joachim 2026-09-16 and pending his pick; swap the strings here only. */
    cutWarningText(edges) {
      if (!edges.length) return "";
      if (edges.length >= 3) {
        return "The edges of your photo are outside the print area and won't be on the cake. Zoom out to bring them back.";
      }
      if (edges.length === 2) {
        return `The ${edges[0]} and ${edges[1]} edges of your photo are outside the print area and won't be on the cake. Drag the photo or zoom out to bring them back.`;
      }
      return `The ${edges[0]} edge of your photo is outside the print area and won't be on the cake. Drag the photo or zoom out to bring it back.`;
    },

    /* Where a contained crop lands inside the photo area, as fractions of that
       area (x, y, w, h, origin top-left).

       This is the parity artefact. The theme draws its preview into this rect;
       renderArtwork's `fit: "contain"` resize produces the same rect in print
       space, because a contain fit is scale-free. The app's parity test
       measures the printed artwork and asserts it lands here. Anything that
       moves one side without the other turns that test red. */
    containRect(cropW, cropH, areaW, areaH) {
      const scale = Math.min(areaW / cropW, areaH / cropH);
      const w = (cropW * scale) / areaW;
      const h = (cropH * scale) / areaH;
      return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
    },

    /* C40: where a STORED crop lands in the photo area, for a reopened design.

       The print extracts the stored crop and then resizes it to the photo area
       (renderArtwork), so whatever aspect the crop was framed at, cover trims
       the overflow equally from both sides of the long axis and contain
       letterboxes. A reopened design whose area has since changed shape (the
       message was added or removed, the placement toggled) must preview that,
       not stretch the crop into the new area.

       `src` is in source pixels, `dest` in fractions of the photo area, the
       same units containRect returns. Nothing here recomputes the crop: the
       stored rect goes in and is only ever read. */
    placeStoredCrop(crop, areaW, areaH, fit) {
      if (fit === "contain") {
        return { src: { ...crop }, dest: this.containRect(crop.width, crop.height, areaW, areaH) };
      }
      const scale = Math.max(areaW / crop.width, areaH / crop.height);
      const width = areaW / scale;
      const height = areaH / scale;
      return {
        src: {
          x: crop.x + (crop.width - width) / 2,
          y: crop.y + (crop.height - height) / 2,
          width,
          height,
        },
        dest: { x: 0, y: 0, w: 1, h: 1 },
      };
    },
  };

  /* Contract v3.5 (C79): emoji in the writing. The print draws the words in
     the chosen font and each emoji from Google's Noto Emoji artwork, which the
     app serves at GET /emoji/<key>.svg. This is the app's split rule
     (app/lib/customiser/writing.ts) restated, so the preview draws, and
     refuses, exactly what the print does. Pure, like TfkCakeGeometry.

     The unit is the grapheme, the character as the customer sees it: "❤️" is
     two code points and "👨‍👩‍👧" five, but each is one character on the cake
     and one towards the limit. */
  const SEGMENTER = typeof Intl !== "undefined" && Intl.Segmenter
    ? new Intl.Segmenter("en", { granularity: "grapheme" })
    : null;
  const PICTOGRAPHIC = /\p{Extended_Pictographic}/u;
  const SYMBOL = /\p{S}/u;
  const EMOJI_BY_DEFAULT = /\p{Emoji_Presentation}/u;
  const VARIATION_SELECTORS = /[︎️]/g;

  const TfkCakeWriting = {
    graphemes(text) {
      if (SEGMENTER) return Array.from(SEGMENTER.segment(text), (s) => s.segment);
      return Array.from(text);
    },

    /* What counts towards the limit: one per character the customer sees,
       line breaks included. */
    length(text) {
      return TfkCakeWriting.graphemes(text).length;
    },

    /* The artwork key: code points in lower-case hex joined by "-", without
       U+FE0F, so "❤️" and "❤" both find the red heart. */
    emojiKey(grapheme) {
      return Array.from(grapheme, (ch) => ch.codePointAt(0))
        .filter((cp) => cp !== 0xfe0f)
        .map((cp) => cp.toString(16))
        .join("-");
    },

    /* The font's coverage from the config's [first, last] ranges. With no
       ranges (the app couldn't read the font file) the font is taken to draw
       everything but pictographs, and the app's own check decides. */
    fontHasFrom(ranges) {
      if (!Array.isArray(ranges)) return (cp) => !PICTOGRAPHIC.test(String.fromCodePoint(cp));
      return (cp) => {
        let lo = 0;
        let hi = ranges.length - 1;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (cp < ranges[mid][0]) hi = mid - 1;
          else if (cp > ranges[mid][1]) lo = mid + 1;
          else return true;
        }
        return false;
      };
    },

    /* Whether the symbol font may draw a code point: Unicode symbols only
       (category S). The font carries Latin letters too, and a letter the
       cake's font lacks stays refused (ruled 15 October 2026). */
    isSymbol(cp) {
      return SYMBOL.test(String.fromCodePoint(cp));
    },

    /* One line split into runs of font text, runs of symbols and single
       emoji, plus what nothing can draw. The app's rule (writing.ts), ruled
       15 October 2026: the emoji form (U+FE0F, or a character that is an
       emoji by default, such as ⭐) is a colour emoji when the artwork has it.
       Anything else is drawn by the font when it has every code point (after
       NFC), then by the symbol font, then as a colour emoji. The variation
       selectors only choose a form and are never drawn. `symbolHas` is
       omitted below contract 3.6, where there is no symbol font. */
    split(line, fontHas, emojiHas, symbolHas) {
      const pieces = [];
      const undrawable = [];
      const pushRun = (kind, text) => {
        const last = pieces[pieces.length - 1];
        if (last && last.kind === kind) last.text += text;
        else pieces.push({ kind, text });
      };
      const draws = (text, has) => Array.from(text).every((ch) => has(ch.codePointAt(0)));
      const symbolDraws = (cp) => !!symbolHas && TfkCakeWriting.isSymbol(cp) && symbolHas(cp);
      for (const grapheme of TfkCakeWriting.graphemes(line)) {
        const text = grapheme.normalize("NFC");
        const plain = text.replace(VARIATION_SELECTORS, "");
        const wantsEmoji = text.includes("️") || (!text.includes("︎") && EMOJI_BY_DEFAULT.test(text));
        const key = TfkCakeWriting.emojiKey(grapheme);
        if (wantsEmoji && emojiHas(key)) { pieces.push({ kind: "emoji", key, grapheme }); continue; }
        if (plain.length > 0 && draws(plain, fontHas)) { pushRun("text", plain); continue; }
        if (plain.length > 0 && draws(plain, symbolDraws)) { pushRun("symbol", plain); continue; }
        if (!wantsEmoji && emojiHas(key)) { pieces.push({ kind: "emoji", key, grapheme }); continue; }
        if (!undrawable.includes(grapheme)) undrawable.push(grapheme);
      }
      return { pieces, undrawable };
    },

    /* The app's refusal, word for word. */
    undrawableMessage(undrawable, fontName) {
      return `We can't print ${undrawable.join(" ")} in ${fontName}. Remove ${undrawable.length === 1 ? "it" : "them"} or choose another font.`;
    },
  };

  /* Same CommonJS shim as tfk-pickup-widget.js:580, for the same reason: the
     theme's node:test suite needs the pure functions without a DOM. */
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { TfkCakeGeometry, TfkCakeWriting };
  }

  class TfkCakeCustomiser extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.completeMode = this.dataset.mode === "complete";
      if (this.completeMode) {
        this.initCompleteMode();
        return;
      }
      this.formatKey = this.dataset.format;
      this.form = this.closest("form");
      this.state = {
        config: null,
        message: "",
        fontKey: null,
        colourKey: null,
        photo: null, // { file, bitmap, key, previewUrl, uploadState: "uploading"|"done"|"error" }
        images: [], // contract v2.7 cupcake multi-image slots (see isCupcakeAllocator)
        pan: { x: 0.5, y: 0.5 }, // crop centre as fraction of source
        zoom: 1, // 1 = cover fit; >1 zooms in
        rightsAccepted: false,
        printAccepted: false,
        lowResAccepted: false,
        saving: false,
        messagePlacement: "overlay", // "overlay" | "band" (contract v2.4)
        messageAnchor: { x: 0.5, y: 0.85 }, // centre of message block, sheet fractions
        orientation: "landscape", // "landscape" | "portrait" (contract v3.0)
        messageSizeMm: null, // contract v3.3: Celebration text size; null = as large as fits
      };
      this.orientationTouched = false; // explicit toggle click beats the tall-photo auto-default
      this.loadedFonts = new Set(); // font.key values whose FontFace has finished loading
      // Mockup map (contract v2): parse before render() wipes the children.
      let mockups = null;
      const mockupJson = this.querySelector("[data-tfk-mockups]");
      try {
        mockups = mockupJson ? JSON.parse(mockupJson.textContent) : null;
      } catch (err) {
        console.warn("[customiser] mockup map parse failed", err);
      }
      this.mockups = mockups || { fallback: null, byVariant: {} };
      if (!this.mockups.byVariant) this.mockups.byVariant = {};
      if (!this.mockups.areaByVariant) this.mockups.areaByVariant = {};
      if (!("areaFallback" in this.mockups)) this.mockups.areaFallback = null;
      this.currentVariantId = this.dataset.initialVariant || null;
      // The bespoke PDP dispatches variant:update (bubbling) from its variant selects.
      (this.closest(".shopify-section") || document).addEventListener("variant:update", (e) => {
        this.currentVariantId = e.detail && e.detail.resource ? e.detail.resource.id : null;
        this.setMockupForVariant(this.currentVariantId);
      });
      // contract v2.7: cupcake allocation. packSize comes from the product
      // metafield custom.pack_size (rendered onto the element); a missing or
      // non-positive value fails safe (blocks add-to-cart, see validate()).
      const ps = parseInt(this.dataset.packSize, 10);
      this.packSize = Number.isInteger(ps) && ps > 0 ? ps : null;
      this._slotSeq = 0;
      // The pool is packSize x the page's NATIVE Shopify quantity selector; the
      // customiser owns no quantity control of its own. Bind to the live value.
      this.qtyInput = this.form ? this.form.querySelector('[name="quantity"]') : null;
      if (this.qtyInput) {
        const onQty = () => this.onNativeQtyChange();
        this.qtyInput.addEventListener("input", onQty);
        this.qtyInput.addEventListener("change", onQty);
      }
      this.loadConfig();
      this.bindSubmit();
    }

    /* Contract v2.8 complete mode: no product form, no native quantity
       selector. The design snapshot from GET /design drives everything;
       formatKey/quantity/packSize are LOCKED (display only). Mockups start
       empty (flat-sheet fallback) and are seeded from the v3.1 mockup block
       in loadConfig when the backend provides one. */
    async initCompleteMode() {
      this.token = this.dataset.token || "";
      /* Contract v2.9: unpaid-checkout states. The GATE lives on the CONFIG
         contract, resolved in loadConfig — the mount node's data-contract is
         hardcoded "2.8" by the app's /complete fragment (2026-08-04 UAT
         finding), so it only gates the mount itself. The raw payment block
         is parked at prefill and adopted once the gate resolves. */
      this.v29 = false;
      this.payment = null;
      this._pendingPayment = null;
      /* Contract v3.1: display { productTitle, variantTitle } + mockup
         { url, area } blocks, parked at prefill like payment and adopted
         once loadConfig resolves the >= 3.1 gate. */
      this.v31 = false;
      this.completeDisplay = null;
      this._pendingDisplay = null;
      /* Contract v3.4 (C70): two or more cakes on one order. The order
         { cakeNumber, cakeCount, waiting } block is parked at prefill like
         payment and display, and adopted once loadConfig resolves the >= 3.4
         gate. `change` is set by the app on the mount node when the customer
         reached this designer through "Change this cake's design" on the
         cake's own page; the save sends it back (withContractFields). */
      this.v34 = false;
      this.completeOrder = null;
      this._pendingOrder = null;
      this.change = this.dataset.change === "1";
      this._pendingMockup = null;
      this.form = null;
      this.qtyInput = null;
      this.mockups = { fallback: null, byVariant: {}, areaFallback: null, areaByVariant: {} };
      this.currentVariantId = null;
      this._slotSeq = 0;
      this.loadedFonts = new Set();
      this.state = {
        config: null,
        message: "",
        fontKey: null,
        colourKey: null,
        photo: null,
        images: [],
        pan: { x: 0.5, y: 0.5 },
        zoom: 1,
        rightsAccepted: false,
        printAccepted: false,
        lowResAccepted: false,
        saving: false,
        messagePlacement: "overlay",
        messageAnchor: { x: 0.5, y: 0.85 },
        orientation: "landscape", // contract v3.0; prefilled from the GET snapshot
        messageSizeMm: null, // contract v3.3; prefilled from the GET snapshot
      };
      this.orientationTouched = false;
      this.innerHTML = "";
      const loading = document.createElement("p");
      loading.className = "tfk-customiser__note";
      loading.textContent = "Loading your design…";
      this.appendChild(loading);
      let res;
      try {
        res = await fetch(`${API_BASE}/design?token=${encodeURIComponent(this.token)}`);
      } catch (err) {
        console.warn("[customiser] design load failed", err);
        this.innerHTML = "";
        this.appendChild(this.buildError("Your design could not be loaded. Please refresh the page."));
        return;
      }
      // The contract documents 409 for PATCH only, but treat it identically
      // to 404 here too, defensively: both mean the token is no longer good.
      if (res.status === 404 || res.status === 409) { this.renderExpired(); return; }
      if (!res.ok) {
        console.warn("[customiser] design load failed", res.status);
        this.innerHTML = "";
        this.appendChild(this.buildError("Your design could not be loaded. Please refresh the page."));
        return;
      }
      const data = await res.json();
      if (data.state === "confirmed") { this.renderConfirmed(false); return; }
      if (data.state !== "editable" || !data.design) {
        this.innerHTML = "";
        this.appendChild(this.buildError("Your design could not be loaded. Please refresh the page."));
        return;
      }
      this.snapshot = data.design;
      // Parked until loadConfig resolves the v2.9/v3.1 gates; plain property
      // reads are safe on any backend (undefined when absent, never a throw).
      this._pendingPayment = data.payment || null;
      this._pendingDisplay = data.display || null;
      this._pendingOrder = data.order || null;
      this._pendingMockup = data.mockup || null;
      this.formatKey = data.design.formatKey;
      const ps = parseInt(data.design.packSize, 10);
      this.packSize = Number.isInteger(ps) && ps > 0 ? ps : null;
      const q = parseInt(data.design.quantity, 10);
      this.lockedQuantity = Number.isInteger(q) && q > 0 ? q : null;
      this.loadConfig();
    }

    /* Shop phone rendered into the loader snippet's JSON island; null when
       the shop has no contact phone set. */
    completePhone() {
      const el = document.getElementById("tfk-complete-data");
      if (!el) return null;
      try { return JSON.parse(el.textContent).phone || null; } catch { return null; }
    }

    changeSentence() {
      const phone = this.completePhone();
      return phone
        ? "If you need to make a change, please call us on " + phone + " and we'll sort it out."
        : "If you need to make a change, please contact the shop and we'll sort it out.";
    }

    renderTerminal(title, text) {
      this.saveBtn = null;
      this.paymentSlot = null;
      this.statusSlot = null;
      this.classList.add("tfk-customiser--terminal");
      this.innerHTML = "";
      const wrap = document.createElement("div");
      wrap.className = "tfk-customiser__terminal";
      const h = document.createElement("h2");
      h.className = "tfk-customiser__terminal-title";
      h.textContent = title;
      const p = document.createElement("p");
      p.textContent = text;
      wrap.append(h, p);
      this.appendChild(wrap);
    }

    /* Distinct copy from the expired panel: this design WAS successfully
       finished. justNow = true right after a PATCH returns state "confirmed". */
    renderConfirmed(justNow) {
      this.renderTerminal(
        justNow ? "Thank you, your design is confirmed" : "Your design is already confirmed",
        (justNow
          ? "We have everything we need and your design has gone through to our kitchen. "
          : "There is nothing more to add for this cake. ") + this.changeSentence(),
      );
    }

    /* Same copy as the server-rendered expired fragment (contract section 4),
       used for in-page 404/409 transitions after the page already mounted. */
    renderExpired() {
      const phone = this.completePhone();
      this.renderTerminal(
        "This link has expired",
        phone
          ? "For changes to your cake order, please call us on " + phone + " and we'll sort it out."
          : "For changes to your cake order, please contact the shop and we'll sort it out.",
      );
    }

    /* Quantity source: complete mode uses the LOCKED quantity from the GET
       /design snapshot (there is no native selector on that page and the
       customer must not change it); PDP mode reads the native selector live. */
    nativeQty() {
      if (this.completeMode) {
        return Number.isInteger(this.lockedQuantity) && this.lockedQuantity > 0 ? this.lockedQuantity : 1;
      }
      const q = parseInt(this.qtyInput && this.qtyInput.value, 10);
      return Number.isInteger(q) && q > 0 ? q : 1;
    }

    onNativeQtyChange() {
      // Pool resizes with quantity; per-image counts are deliberately NOT
      // rescaled (contract v2.7); just re-block until the customer rebalances.
      if (!this.isCupcakeAllocator() || !this.readoutEl) return;
      this.updateAllocationReadout();
      this.refreshValidity();
    }

    async loadConfig() {
      try {
        const res = await fetch(`${API_BASE}/config?format=${encodeURIComponent(this.formatKey)}`);
        if (!res.ok) throw new Error(`config ${res.status}`);
        this.state.config = await res.json();
        const cfg = this.state.config;
        // contract v2.4/v2.4.1: message overlay gated on contract >= 2.4 and the
        // marginMm/dpi geometry fields the shared text rule depends on.
        this.v24 = !!(
          cfg.contract &&
          parseFloat(cfg.contract) >= 2.4 &&
          typeof cfg.format.marginMm === "number" &&
          typeof cfg.format.dpi === "number"
        );
        // contract v2.6: multi-line messages (textarea, auto-wrap, multi-line
        // sizing) + text-only celebration formats. Depends on the v2.4 geometry
        // gate (marginMm/dpi drive the shared sizing rule). When the deployed
        // backend still reports < 2.6, every v2.6 branch falls through to exact
        // v2.4 behaviour. parseFloat is correct for the current "2.4"/"2.6"
        // scheme (a future "2.10" would need real semver comparison).
        this.v26 = !!(this.v24 && parseFloat(cfg.contract) >= 2.6);
        // contract v2.7: cupcake multi-image allocation. Gated purely on the
        // contract version (no extra geometry fields). When the backend is
        // < 2.7 the format falls through to the single-photo path (fail closed);
        // the theme never sends count/quantity/packSize below this version.
        this.v27 = !!(cfg.contract && parseFloat(cfg.contract) >= 2.7);
        // Contract v2.9/v3.0 gates. BOTH modes read the version off the
        // CONFIG contract: the completion mount node's data-contract is
        // hardcoded "2.8" by the app's /complete fragment (2026-08-04 UAT
        // finding, flagged back to the app team), so it cannot gate the
        // newer features — the docs allow the config-field convention for
        // exactly this reason. parseFloat stays safe: the committed version
        // after "2.9" was "3.0", never "2.10".
        if (this.completeMode) {
          this.v29 = !!(cfg.contract && parseFloat(cfg.contract) >= 2.9);
          this.payment = this.v29 && this._pendingPayment ? this._pendingPayment : null;
          // Contract v3.1: flavour line + on-cake mockup. The mockup block
          // seeds the SAME machinery the PDP metafield island fills, so
          // canonical framing, the area override (raw landscape fractions,
          // validated and portrait-rotated by mockupAreaOverride) and the
          // flat-sheet fallback on null all apply unchanged.
          this.v31 = !!(cfg.contract && parseFloat(cfg.contract) >= 3.1);
          if (this.v31) {
            this.completeDisplay = this._pendingDisplay;
            const mock = this._pendingMockup;
            if (mock && typeof mock.url === "string" && mock.url) {
              this.mockups.fallback = mock.url;
              if (typeof mock.area === "string") this.mockups.areaFallback = mock.area;
            }
          }
          // Contract v3.4 (C70): the order block. Null for an order with one
          // cake, which leaves every line and every answer below as it was.
          this.v34 = !!(cfg.contract && parseFloat(cfg.contract) >= 3.4);
          this.completeOrder = this.v34 && this._pendingOrder ? this._pendingOrder : null;
        }
        // Contract v3.0: portrait orientation. The control gate is contract
        // >= 3.0 AND the format's rotatable flag — NEVER inferred from sheet
        // dimensions. The PDP reads rotatable off config; complete mode reads
        // it off the GET /design snapshot (build-note pinned detail B).
        const v30Contract = !!(cfg.contract && parseFloat(cfg.contract) >= 3.0);
        const rotatable = this.completeMode
          ? !!(this.snapshot && this.snapshot.rotatable === true)
          : cfg.format.rotatable === true;
        this.v30 = !!(v30Contract && rotatable);
        // Contract v3.3 (C44): the Celebration text size. Below 3.3 the backend
        // would drop messageSizeMm and print the fitted size, so the slider is
        // not offered at all (ruled 29 September 2026).
        this.v33 = !!(cfg.contract && parseFloat(cfg.contract) >= 3.3);
        // Contract v3.5 (C79): emoji drawn from the app's Noto artwork, the
        // limit counted by what the customer sees, and characters the print
        // can't draw refused here. Below 3.5 the preview draws exactly as
        // before (the browser's own emoji), which is what that backend prints
        // too (an empty box), so nothing about an older backend changes.
        this.v35 = !!(
          cfg.contract &&
          parseFloat(cfg.contract) >= 3.5 &&
          cfg.emoji &&
          typeof cfg.emoji.url === "string" &&
          cfg.emoji.sizeEm > 0 &&
          cfg.emoji.advanceEm > 0
        );
        // Contract v3.6 (C79): text symbols the font lacks, such as ★, drawn
        // from the app's symbol font in the writing's colour, as the print
        // draws them. Below 3.6 a symbol is refused, as the 3.5 app refuses it.
        this.v36 = !!(
          this.v35 &&
          parseFloat(cfg.contract) >= 3.6 &&
          cfg.symbols &&
          typeof cfg.symbols.url === "string" &&
          Array.isArray(cfg.symbols.codePoints)
        );
        // Default the font and colour so a message alone never blocks
        // add-to-cart. The palette leads with pale warn-tier colours (white
        // first), which are illegible on most photos, so the default is the
        // first colour not flagged warn.
        const fonts = cfg.fonts || [];
        const colours = cfg.colours || [];
        if (!this.state.fontKey && fonts.length) {
          this.state.fontKey = fonts[0].key;
        }
        if (!this.state.colourKey && colours.length) {
          const dark = colours.find((c) => c.printQuality !== "warn") || colours[0];
          this.state.colourKey = dark.key;
        }
        if (this.completeMode) this.applyCompletePrefill();
        this.render();
        // C40: after render, so the canvas and slot canvases exist to redraw.
        if (this.completeMode) this.hydrateRemotePhotos();
      } catch (err) {
        console.warn("[customiser] config failed", err);
        this.innerHTML = "";
        this.appendChild(this.buildError("Personalisation options could not be loaded. Please refresh the page."));
        this.setSubmitBlocked(true, "Personalisation unavailable");
      }
    }

    /* Contract v2.8: prefill editable fields from the GET /design snapshot.
       Runs after loadConfig's defaults so stored values win over defaults.
       Stored photos become "remote" entries: their stored crop (and fit) is
       passed through untouched, and they are replaced only when the customer
       uploads. Since C40 the GET response carries a signed previewUrl on each
       one, which hydrateRemotePhotos fetches and decodes so the preview shows
       the photo. Until then, or if that fails, the entry is the on-file
       placeholder it always was. */
    applyCompletePrefill() {
      const d = this.snapshot;
      const cfg = this.state.config;
      // Contract v3.0: the GET always reports "landscape" or "portrait".
      if (d.orientation === "portrait") this.state.orientation = "portrait";
      if (typeof d.message === "string" && d.message.length) this.state.message = d.message;
      // Contract v3.3: the stored text size, kept in millimetres (not as a slider
      // position) so a font that finishes loading later can't move it.
      if (typeof d.messageSizeMm === "number" && d.messageSizeMm > 0) this.state.messageSizeMm = d.messageSizeMm;
      if (d.fontKey) this.state.fontKey = d.fontKey;
      if (d.colourKey) this.state.colourKey = d.colourKey;
      if (d.messageLayout && d.messageLayout.placement === "overlay") {
        this.state.messagePlacement = "overlay";
        if (d.messageLayout.anchor) {
          this.state.messageAnchor = { x: d.messageLayout.anchor.x, y: d.messageLayout.anchor.y };
        }
      } else if (d.message) {
        this.state.messagePlacement = "band";
      }
      const photos = Array.isArray(d.photos) ? d.photos : [];
      if (!photos.length) return;
      if (this.isCupcakeAllocator()) {
        this.state.images = photos.map((p) => ({
          id: ++this._slotSeq,
          file: null,
          bitmap: null,
          key: p.key,
          previewUrl: p.previewUrl || null,
          uploadState: "done",
          pan: { x: 0.5, y: 0.5 },
          zoom: 1,
          count: Number.isInteger(p.count) && p.count >= 1 ? p.count : 1,
          remote: true,
          crop: p.crop,
        }));
      } else if (cfg.format.supportsPhoto) {
        this.state.photo = {
          file: null,
          bitmap: null,
          key: photos[0].key,
          previewUrl: photos[0].previewUrl || null,
          uploadState: "done",
          remote: true,
          crop: photos[0].crop,
          // v3.2: carried so a PATCH that re-sends this photo re-sends its fit.
          // Without it a stored contained design would silently revert to cover.
          fit: photos[0].fit === "contain" ? "contain" : undefined,
        };
      }
    }

    /* C40: fetch and decode each stored photo from its signed previewUrl, so a
       reopened design draws the photo it already has. The entry stays REMOTE
       once it has a bitmap: everything that would otherwise recompute a crop
       from the pan and zoom (computeCrop, containFit, slabPhotoPayload,
       computeCropForSlot) keys on `remote`, not on the bitmap, so decoding a
       stored photo never reframes it.

       Failure is quiet by design. A URL that has expired, a network error or an
       undecodable image leaves the on-file placeholder in place, and the save
       still keeps the photo, because an untouched stored photo is left out of
       the PATCH. A photo the customer replaced or removed while this was in
       flight is not written to. */
    async hydrateRemotePhotos() {
      const cupcake = this.isCupcakeAllocator();
      const entries = cupcake ? this.state.images.slice() : this.state.photo ? [this.state.photo] : [];
      await Promise.all(
        entries
          .filter((entry) => entry.remote && entry.previewUrl && !entry.bitmap)
          .map(async (entry) => {
            let bitmap;
            try {
              const res = await fetch(entry.previewUrl);
              if (!res.ok) throw new Error("HTTP " + res.status);
              bitmap = await createImageBitmap(await res.blob()); // EXIF-upright, as at upload
            } catch (err) {
              console.warn("[customiser] stored photo preview failed", err);
              return;
            }
            const current = cupcake ? this.state.images.includes(entry) : this.state.photo === entry;
            if (!current) return;
            entry.bitmap = bitmap;
            if (cupcake) this.drawSlot(entry);
            else this.drawPreview();
          }),
      );
    }

    formatPickup(pickup) {
      if (!pickup || !pickup.date) return null;
      const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const dp = pickup.date.split("-").map(Number);
      let text = "Pickup " + dp[2] + " " + (months[dp[1] - 1] || "") + " " + dp[0];
      if (pickup.time) {
        const hm = pickup.time.split(":").map(Number);
        const hour12 = hm[0] % 12 === 0 ? 12 : hm[0] % 12;
        text += " at " + hour12 + ":" + String(hm[1]).padStart(2, "0") + (hm[0] < 12 ? " am" : " pm");
      }
      if (pickup.location) text += ", " + pickup.location;
      return text;
    }

    /* ---- Contract v3.4 (C70): two or more cakes on one order ----
       One order can hold two or more designed cakes. Each cake keeps its own
       link and its own designer; the app tells the designer which cake this
       is and how many others still wait (the `order` block). Three things
       follow, all gated on the config contract (v34), and none of them
       changes anything for an order with one cake, whose block is null:
         - the first line names the cake ("Cake 1 of 2 on order #60053");
         - after a save, when another cake waits, the page reloads to the
           cake's own page, which the app draws, in place of the "We have
           everything we need" panel that was true of that cake only;
         - a save says which contract it speaks and whether the customer
           chose to change a cake that already had its design, so the app
           can refuse a save from a designer left open (the Back button
           after a save, a second tab) before it replaces the first cake's
           photo with the second. */

    /* The first locked line: the order, and this cake's number on it when
       the order has more than one. Null when there is neither. */
    orderLine(orderRef) {
      const order = this.v34 ? this.completeOrder : null;
      if (order && order.cakeCount > 1) {
        return "Cake " + order.cakeNumber + " of " + order.cakeCount + (orderRef ? " on order " + orderRef : "");
      }
      return orderRef ? "Order " + orderRef : null;
    }

    /* Adds the two v3.4 fields to a PATCH body. Below 3.4 the body is as it
       always was, so an older backend never sees a field it doesn't know. */
    withContractFields(body) {
      if (!this.v34) return body;
      body.contract = THEME_CONTRACT;
      if (this.change) body.change = true;
      return body;
    }

    /* True when the save's answer says another cake on the order still waits. */
    anotherCakeWaits(data) {
      return !!(this.v34 && data && data.order && data.order.cakeCount > 1 && data.order.waiting > 0);
    }

    /* True for the app's refusal of a save that would replace a designed
       cake's design on an order with more than one cake (409, state
       "designed"). Any other 409 is the expired panel, as before. */
    alreadyDesigned(data) {
      return !!(data && data.state === "designed");
    }

    /* The cake's own page: the same address with this cake's link. The app
       draws it (the designed cake, and the cake still waiting). `saved`
       says the customer has just saved, so the page thanks them. */
    cakePageUrl(pathname, saved) {
      return pathname + "?token=" + encodeURIComponent(this.token) + (saved ? "&saved=1" : "");
    }

    goToCakePage(saved) {
      window.location.assign(this.cakePageUrl(window.location.pathname, saved));
    }

    /* Locked fields (contract v2.8): formatKey, quantity, packSize are shown
       as plain text and NEVER sent on PATCH. orderRef displays as-is. */
    buildLockedSummaryRow() {
      const d = this.snapshot;
      const cfg = this.state.config;
      const row = document.createElement("div");
      row.className = "tfk-customiser__row tfk-customiser__locked";
      const lines = [];
      const orderLine = this.orderLine(d.orderRef);
      if (orderLine) lines.push(orderLine);
      let what = cfg.format.name;
      // Contract v3.1: the ordered product/flavour, exactly the values the
      // confirmation emails print (comma-joined, PR 146 convention). Falls
      // back to the format name when null; "Default Title" is Shopify's
      // single-variant placeholder, never customer copy.
      if (this.v31 && this.completeDisplay) {
        if (this.completeDisplay.productTitle) what = this.completeDisplay.productTitle;
        const flavour = this.completeDisplay.variantTitle;
        if (flavour && flavour !== "Default Title") what += ", " + flavour;
      }
      if (this.isCupcakeAllocator() && this.packSize) {
        what += ", pack of " + this.packSize;
        if (this.lockedQuantity > 1) what += " × " + this.lockedQuantity;
        what += " (" + this.poolSize() + " cupcakes)";
      } else if (this.lockedQuantity > 1) {
        what += " × " + this.lockedQuantity;
      }
      lines.push(what);
      const pickup = this.formatPickup(d.pickup);
      if (pickup) lines.push(pickup);
      lines.forEach((text, i) => {
        const p = document.createElement("p");
        p.className = (i === 0 ? "tfk-customiser__label" : "tfk-customiser__note") + " tfk-customiser__locked-line";
        p.textContent = text;
        row.appendChild(p);
      });
      return row;
    }

    buildError(text) {
      const el = document.createElement("p");
      el.className = "tfk-customiser__error";
      el.textContent = text;
      return el;
    }

    setSubmitBlocked(blocked, reason) {
      if (this.completeMode) {
        if (!this.saveBtn) return;
        this.saveBtn.disabled = blocked;
        this.saveBtn.title = blocked && reason ? reason : "";
        return;
      }
      const btn = this.form && this.form.querySelector(".tfk-product__add-to-cart");
      if (!btn) return;
      btn.disabled = blocked;
      btn.dataset.customiserBlocked = blocked ? "1" : "";
      if (reason) btn.title = blocked ? reason : "";
    }

    render() {
      const cfg = this.state.config;
      this.innerHTML = "";
      const heading = document.createElement("h3");
      heading.className = "tfk-customiser__heading";
      heading.textContent = this.completeMode ? "Finish your cake design" : "Personalise your cake";
      this.appendChild(heading);
      if (this.completeMode) {
        this.appendChild(this.buildLockedSummaryRow());
        this.paymentSlot = document.createElement("div");
        this.appendChild(this.paymentSlot);
        this.renderUnpaidBanner();
      }

      // contract v2.7: photo-only formats (cupcake-disc) get the multi-image
      // disc-allocation UI instead of the single-photo/preview rows.
      if (this.isCupcakeAllocator()) {
        this.appendChild(this.buildAllocatorRow());
        this.appendChild(this.buildRightsRow());
        this.appendChild(this.buildPrintNoticeRow());
        this.errorSlot = document.createElement("div");
        this.appendChild(this.errorSlot);
        if (this.completeMode) this.appendChild(this.buildSaveRow());
        this.refreshValidity();
        return;
      }

      if (cfg.format.supportsPhoto) this.appendChild(this.buildPhotoRow());   // Task 3
      this.appendChild(this.buildPreviewRow());                                // Task 4
      if (cfg.format.supportsMessage) this.appendChild(this.buildMessageRow());
      if (this.v24 && cfg.format.supportsMessage && !this.isCelebration()) this.appendChild(this.buildStripToggleRow());
      if (cfg.format.supportsMessage) this.appendChild(this.buildFontRow());
      if (cfg.format.supportsMessage) this.appendChild(this.buildColourRow());
      if (cfg.format.supportsPhoto) this.appendChild(this.buildRightsRow());   // Task 5
      if (cfg.format.supportsPhoto) {
        this.lowResRow = this.buildLowResRow();
        this.appendChild(this.lowResRow);
      }
      this.appendChild(this.buildPrintNoticeRow());
      this.errorSlot = document.createElement("div");
      this.appendChild(this.errorSlot);
      if (this.completeMode) this.appendChild(this.buildSaveRow());
      this.refreshValidity();
    }

    buildMessageRow() {
      const cfg = this.state.config;
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      const label = document.createElement("label");
      label.className = "tfk-customiser__label";
      label.htmlFor = "tfk-customiser-message";
      label.textContent = cfg.format.messageRequired
        ? "Message on cake"
        : "Message on cake (optional, leave blank for photo only)";
      // contract v2.6: a textarea (Enter = line break) replaces the single-line
      // input so customers can write multi-line messages. maxLength still caps
      // total characters; the counter counts newlines too (textarea.value keeps
      // them). Fails closed to the v2.4 text input when the backend is < 2.6.
      const input = this.v26 ? document.createElement("textarea") : document.createElement("input");
      if (this.v26) input.rows = 2;
      else input.type = "text";
      input.id = "tfk-customiser-message";
      input.className = "tfk-customiser__message-input";
      // Contract v3.5: maxLength counts UTF-16 units, so one emoji would use
      // two to eleven of the limit. The limit is then counted by what the
      // customer sees (validate), and maxLength only stops runaway pasting.
      input.maxLength = this.v35 ? cfg.format.messageMaxChars * 4 : cfg.format.messageMaxChars;
      input.placeholder = this.v26 ? "Enter your message (press Enter for a new line)" : "Enter your message";
      input.value = this.state.message;
      input.addEventListener("input", () => {
        this.state.message = input.value;
        this.drawPreview();
        this.refreshValidity();
      });
      const counter = document.createElement("span");
      counter.className = "tfk-customiser__note";
      const updateCounter = () => {
        const used = this.v35 ? TfkCakeWriting.length(input.value) : input.value.length;
        counter.textContent = `${used}/${cfg.format.messageMaxChars} characters`;
      };
      input.addEventListener("input", updateCounter);
      updateCounter();
      row.append(label, input, counter);
      if (this.v35) {
        // Contract v3.5: says which characters can't be printed, or that the
        // message is over the limit. Visible, because the blocked button's
        // reason is only a tooltip, which a phone never shows.
        this.writingSlot = document.createElement("div");
        this.writingWarning = "";
        row.appendChild(this.writingSlot);
      }
      if (this.v26) {
        // contract v2.6 legibility advisory slot (non-blocking); updated from
        // drawMessage once the uniform font size is known.
        this.legibilitySlot = document.createElement("div");
        this.lowLegibility = false;
        row.appendChild(this.legibilitySlot);
      }
      return row;
    }

    /* Contract v2.4: overlay is the default; this checkbox switches to the
       classic band-below-photo layout. Pattern mirrors buildRightsRow(). */
    buildStripToggleRow() {
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      const label = document.createElement("label");
      label.className = "tfk-customiser__note";
      const box = document.createElement("input");
      box.type = "checkbox";
      box.id = "tfk-customiser-strip";
      box.checked = this.state.messagePlacement === "band";
      box.addEventListener("change", () => {
        this.state.messagePlacement = box.checked ? "band" : "overlay";
        this.clampPan();
        this.drawPreview();
      });
      label.append(box, document.createTextNode(" White strip for message"));
      row.appendChild(label);
      return row;
    }

    /* Contract v2.4: "band" when the backend does not support the overlay
       gate, else whatever the customer has chosen (default "overlay"). */
    effectivePlacement() {
      if (!this.v24) return "band";
      // Celebration formats (contract v2.6, supportsPhoto === false) render
      // centred text-only on the white sheet via the band path, never the
      // draggable overlay, so the submit path omits messageLayout and
      // _MessagePlacement (both are gated on placement === "overlay").
      if (this.isCelebration()) return "band";
      return this.state.messagePlacement;
    }

    /* Contract v2.6: a text-only celebration format cannot carry a photo
       (supportsPhoto === false). The photo, rights, and low-res rows are
       already skipped by the supportsPhoto guards in render(). */
    isCelebration() {
      return !!(this.state.config && this.state.config.format.supportsPhoto === false);
    }

    /* ---- Contract v3.0: portrait orientation ---- */

    /* All print-space geometry works in the rotated sheet rect when the
       design is portrait: every consumer of the physical dims (sheet aspect,
       margin inset, sizing floor, legibility, low-res warn) reads these
       effective values instead of the raw config fields. */
    isPortrait() {
      return this.state.orientation === "portrait";
    }
    sheetWMm() {
      const f = this.state.config.format;
      return this.isPortrait() ? f.sheetHeightMm : f.sheetWidthMm;
    }
    sheetHMm() {
      const f = this.state.config.format;
      return this.isPortrait() ? f.sheetWidthMm : f.sheetHeightMm;
    }

    /* Labelled group box for a preview control (Zoom, Orientation): a
       fieldset so the heading sits natively in the border gap and screen
       readers announce it with the group. Fieldsets are left display:block
       (flexing a fieldset breaks legend rendering in some engines); the
       inner body div does the layout. */
    buildControlGroup(title, control, variant) {
      const box = document.createElement("fieldset");
      box.className = "tfk-customiser__control-group tfk-customiser__control-group--" + variant;
      const legend = document.createElement("legend");
      legend.className = "tfk-customiser__control-title";
      legend.textContent = title;
      const body = document.createElement("div");
      body.className = "tfk-customiser__control-body";
      body.appendChild(control);
      box.append(legend, body);
      return box;
    }

    /* Sheet-shaped icon for the orientation buttons: a rounded rect, wide
       for landscape, tall for portrait. Inline SVG, stroke currentColor so
       the button's colour states carry through. */
    orientationIcon(portrait) {
      const NS = "http://www.w3.org/2000/svg";
      const svg = document.createElementNS(NS, "svg");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.setAttribute("width", "22");
      svg.setAttribute("height", "22");
      svg.setAttribute("aria-hidden", "true");
      const rect = document.createElementNS(NS, "rect");
      rect.setAttribute("x", portrait ? "6.5" : "3.5");
      rect.setAttribute("y", portrait ? "3.5" : "6.5");
      rect.setAttribute("width", portrait ? "11" : "17");
      rect.setAttribute("height", portrait ? "17" : "11");
      rect.setAttribute("rx", "2");
      rect.setAttribute("fill", "none");
      rect.setAttribute("stroke", "currentColor");
      rect.setAttribute("stroke-width", "2");
      svg.appendChild(rect);
      return svg;
    }

    /* Orientation is a DESIGN property, not a photo property (owner
       amendment 2026-08-04): the control renders whenever v30 allows it,
       before any photo and on message-only celebration formats alike, as a
       landscape | portrait icon pair beside the zoom bar. The cupcake
       allocator never shows it even if an operator flips rotatable on the
       disc format — rotating a round disc pool is meaningless and the
       allocator UI has no sheet to rotate. */
    buildOrientationControl() {
      const wrap = document.createElement("div");
      wrap.className = "tfk-customiser__orientation";
      wrap.setAttribute("role", "group");
      wrap.setAttribute("aria-label", "Cake orientation");
      this.orientButtons = {};
      [["landscape", "Landscape cake"], ["portrait", "Portrait cake"]].forEach(([key, name]) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "tfk-customiser__orient";
        btn.title = name;
        btn.setAttribute("aria-label", name);
        btn.setAttribute("aria-pressed", String(this.state.orientation === key));
        btn.appendChild(this.orientationIcon(key === "portrait"));
        btn.addEventListener("click", () => {
          this.orientationTouched = true;
          this.setOrientation(key);
        });
        this.orientButtons[key] = btn;
        wrap.appendChild(btn);
      });
      return wrap;
    }

    setOrientation(orientation) {
      if (this.state.orientation === orientation) return;
      // C44: a shrunk Celebration message keeps its slider position across the
      // flip, as a share of what fits, rather than its millimetres. The other
      // orientation fits a different size, and "70% of what fits" is what the
      // customer set.
      const ctx = this.canvas && this.canvas.getContext("2d");
      const share = ctx && this.textSizeCapMm(ctx) != null ? this.state.messageSizeMm / this.fittedSizeMm(ctx) : null;
      this.state.orientation = orientation;
      if (share != null) this.setTextSizeShare(ctx, share);
      if (this.orientButtons) {
        this.orientButtons.landscape.setAttribute("aria-pressed", String(orientation === "landscape"));
        this.orientButtons.portrait.setAttribute("aria-pressed", String(orientation === "portrait"));
      }
      this.clampPan();
      this.drawPreview();
    }

    buildFontRow() {
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      const label = document.createElement("span");
      label.className = "tfk-customiser__label";
      label.textContent = "Font";
      const swatches = document.createElement("div");
      swatches.className = "tfk-customiser__swatches";
      this.fontNote = document.createElement("p");
      this.fontNote.className = "tfk-customiser__note";
      this.fontNote.textContent = "Font preview is approximate.";
      this.fontNote.hidden = true;
      this.state.config.fonts.forEach((font) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "tfk-customiser__swatch";
        btn.textContent = font.name;
        btn.setAttribute("aria-pressed", String(this.state.fontKey === font.key));
        if (font.woff2Url) {
          const face = new FontFace(`tfk-customiser-${font.key}`, `url(${font.woff2Url})`);
          face.load().then((loaded) => {
            document.fonts.add(loaded);
            this.loadedFonts.add(font.key);
            btn.style.fontFamily = `tfk-customiser-${font.key}`;
            this.drawPreview();
            this.updateFontNote();
          }).catch(() => {
            /* preview falls back to body font */
            this.updateFontNote();
          });
        }
        btn.addEventListener("click", () => {
          this.state.fontKey = font.key;
          swatches.querySelectorAll("button").forEach((b) => b.setAttribute("aria-pressed", "false"));
          btn.setAttribute("aria-pressed", "true");
          this.drawPreview();
          this.refreshValidity();
          this.updateFontNote();
        });
        swatches.appendChild(btn);
      });
      row.append(label, swatches, this.fontNote);
      this.updateFontNote();
      return row;
    }

    /* Shows "preview is approximate" when the selected font has no woff2Url,
       or its FontFace load has not (yet, or ever) succeeded. */
    updateFontNote() {
      if (!this.fontNote) return;
      const font = this.state.config.fonts.find((f) => f.key === this.state.fontKey);
      if (!font) { this.fontNote.hidden = true; return; }
      const approximate = !font.woff2Url || !this.loadedFonts.has(font.key);
      this.fontNote.hidden = !approximate;
    }

    buildColourRow() {
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      const label = document.createElement("span");
      label.className = "tfk-customiser__label";
      label.textContent = "Message colour";
      const swatches = document.createElement("div");
      swatches.className = "tfk-customiser__swatches";
      this.colourWarnSlot = document.createElement("div");
      // Celebration cakes always print on the white sheet, so warn-tier colours
      // (the pale ones flagged for photos, and white itself) have no place here;
      // offer only print-safe colours. The default colour is already non-warn,
      // so it stays selectable. Fall back to the full set in the unlikely case
      // that every colour is flagged warn.
      let colours = this.state.config.colours;
      if (this.isCelebration()) {
        const safe = colours.filter((c) => c.printQuality !== "warn");
        if (safe.length) colours = safe;
      }
      colours.forEach((colour) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "tfk-customiser__swatch tfk-customiser__swatch--colour";
        btn.style.background = colour.hex; /* dynamic data, not theme CSS, allowed */
        btn.setAttribute("aria-label", colour.name);
        btn.setAttribute("aria-pressed", String(this.state.colourKey === colour.key));
        btn.addEventListener("click", () => {
          this.state.colourKey = colour.key;
          swatches.querySelectorAll("button").forEach((b) => b.setAttribute("aria-pressed", "false"));
          btn.setAttribute("aria-pressed", "true");
          this.renderColourNote(colour);
          this.drawPreview();
          this.refreshValidity();
        });
        swatches.appendChild(btn);
      });
      const selected = this.state.config.colours.find((c) => c.key === this.state.colourKey);
      if (selected) this.renderColourNote(selected);
      row.append(label, swatches, this.colourWarnSlot);
      return row;
    }

    renderColourNote(colour) {
      this.colourWarnSlot.innerHTML = "";
      const el = document.createElement("p");
      if (colour.printQuality === "warn" && colour.warningText) {
        el.className = "tfk-customiser__warning";
        el.textContent = `${colour.name}: ${colour.warningText}`;
      } else {
        el.className = "tfk-customiser__note";
        el.textContent = colour.name;
      }
      this.colourWarnSlot.appendChild(el);
    }

    validate() {
      const cfg = this.state.config;
      if (!cfg) return { ok: false, reason: "Personalisation still loading" };
      if (this.isCupcakeAllocator()) return this.validateCupcake();
      const hasMessage = this.state.message.trim().length > 0;
      const hasPhoto = !!(this.state.photo && this.state.photo.uploadState === "done");
      const hasLocalPhoto = !!(this.state.photo && !this.state.photo.remote && this.state.photo.uploadState === "done");
      if (cfg.format.messageRequired && !hasMessage) return { ok: false, reason: "Please enter a message" };
      // Contract v2.8: on the completion page a photo is required for EVERY
      // photo-supporting format, message-capable ones included. The server
      // only enforces this for the staff-entry carve-out, so this client
      // gate is the only guard for now (server side tracked on PR-6).
      if (this.completeMode && cfg.format.supportsPhoto && !hasPhoto)
        return { ok: false, reason: "Please add a photo" };
      if (cfg.format.supportsPhoto && !cfg.format.supportsMessage && !hasPhoto)
        return { ok: false, reason: "Please add a photo" };
      if (!hasMessage && !hasPhoto) return { ok: false, reason: "Add a photo or a message" };
      if (hasMessage && (!this.state.fontKey || !this.state.colourKey))
        return { ok: false, reason: "Choose a font and colour for your message" };
      const writingProblem = hasMessage ? this.writingProblem() : null;
      if (writingProblem) return { ok: false, reason: writingProblem };
      if (this.state.photo && this.state.photo.uploadState === "uploading")
        return { ok: false, reason: "Photo still uploading" };
      if (this.state.photo && this.state.photo.uploadState === "error")
        return { ok: false, reason: "Photo upload failed, try again" };
      if ((this.completeMode ? hasLocalPhoto : hasPhoto) && !this.state.rightsAccepted)
        return { ok: false, reason: "Please confirm you have the right to use this image" };
      if (this.lowResActive && !this.state.lowResAccepted)
        return { ok: false, reason: "Please acknowledge the photo may print blurry" };
      if (!this.state.printAccepted)
        return { ok: false, reason: "Please confirm you understand how the printed cake may differ from the preview" };
      if (this.state.saving) return { ok: false, reason: "Saving design" };
      return { ok: true };
    }

    /* contract v2.7 cupcake gate: mirror the server's strict allocation so the
       button reflects it, but the server stays source of truth (its 422 is
       shown verbatim). Sum rule Σcount == packSize x quantity, count >= 1 each,
       hard cap 500. */
    validateCupcake() {
      const images = this.state.images;
      if (!images.length) return { ok: false, reason: "Please add at least one photo" };
      if (images.some((im) => im.uploadState === "uploading")) return { ok: false, reason: "A photo is still uploading" };
      if (images.some((im) => im.uploadState === "error")) return { ok: false, reason: "A photo failed to upload, remove it and try again" };
      if (images.some((im) => !(im.count >= 1))) return { ok: false, reason: "Every photo needs at least one cupcake" };
      const pool = this.poolSize();
      if (pool == null) return { ok: false, reason: "Pack size is unavailable, please contact us" };
      if (pool > 500) return { ok: false, reason: "Personalisation is limited to 500 cupcakes per order. Please lower the quantity or contact us." };
      const allocated = this.allocatedCount();
      if (allocated !== pool) {
        const diff = pool - allocated;
        return {
          ok: false,
          reason: diff > 0
            ? `Please allocate all ${pool} cupcakes (${diff} remaining)`
            : `You've allocated ${allocated} of ${pool} cupcakes`,
        };
      }
      const anyLocal = images.some((im) => !im.remote);
      if ((this.completeMode ? anyLocal : true) && !this.state.rightsAccepted)
        return { ok: false, reason: "Please confirm you have the right to use these images" };
      if (!this.state.printAccepted) return { ok: false, reason: "Please confirm you understand how the printed cake may differ from the preview" };
      if (this.state.saving) return { ok: false, reason: "Saving design" };
      return { ok: true };
    }

    refreshValidity() {
      const v = this.validate();
      this.setSubmitBlocked(!v.ok, v.reason || "");
      this.updateWritingWarning();
    }

    /* ---- Contract v3.5 (C79): emoji in the writing ---- */

    /* Why the writing can't be saved as it is, or null. Over the limit first,
       then characters neither the font nor the emoji artwork draws, in the
       app's own words. Null below contract 3.5 and while the config loads. */
    writingProblem() {
      if (!this.v35 || !this.state.config) return null;
      const max = this.state.config.format.messageMaxChars;
      // The canonical lines count the same as the baked message the app
      // checks: a wrap turns a space into a line break, one for one.
      if (TfkCakeWriting.length(this.canonicalTypedLines().join("\n")) > max) return `Your message is over ${max} characters. Please shorten it.`;
      const undrawable = [];
      for (const line of this.canonicalTypedLines()) {
        for (const g of this.splitWriting(line).undrawable) if (!undrawable.includes(g)) undrawable.push(g);
      }
      if (!undrawable.length) return null;
      const font = (this.state.config.fonts || []).find((f) => f.key === this.state.fontKey);
      return TfkCakeWriting.undrawableMessage(undrawable, font ? font.name : "this font");
    }

    /* Shows writingProblem under the message box. DOM writes only when the
       text changes, since this runs on every keystroke. */
    updateWritingWarning() {
      if (!this.writingSlot) return;
      const text = this.state.message.trim() ? this.writingProblem() || "" : "";
      if (text === this.writingWarning) return;
      this.writingWarning = text;
      this.writingSlot.innerHTML = "";
      if (text) {
        const warn = document.createElement("p");
        warn.className = "tfk-customiser__warning";
        warn.textContent = text;
        this.writingSlot.appendChild(warn);
      }
    }

    /* One line split for the chosen font. Fetches the artwork of each emoji
       it meets, and from v3.6 the symbol font when a symbol needs it, so a
       later frame can draw them. */
    splitWriting(line) {
      const font = (this.state.config.fonts || []).find((f) => f.key === this.state.fontKey);
      const fontHas = TfkCakeWriting.fontHasFrom(font ? font.codePoints : null);
      const symbolHas = this.v36 ? TfkCakeWriting.fontHasFrom(this.state.config.symbols.codePoints) : undefined;
      const split = TfkCakeWriting.split(line, fontHas, (key) => this.emojiArtState(key) !== "missing", symbolHas);
      for (const piece of split.pieces) {
        if (piece.kind === "emoji") this.requestEmojiArt(piece.key);
        if (piece.kind === "symbol") this.requestSymbolFont();
      }
      return split;
    }

    /* Whether a line is drawn piece by piece: it holds an emoji, a symbol or
       a character nothing can draw. A refused character is left out of the
       drawing (ruled 15 October 2026), so the preview never borrows a system
       font for it; the warning under the message box says why. */
    isPiecewise(split) {
      return split.undrawable.length > 0 || split.pieces.some((p) => p.kind !== "text");
    }

    /* Loads the app's symbol font once, the file the print draws symbols
       with. Until it has loaded a symbol takes its slot and draws nothing; if
       it can't load, the app's check decides at save. */
    requestSymbolFont() {
      if (this.symbolFontState) return;
      this.symbolFontState = "loading";
      if (typeof FontFace === "undefined" || typeof document === "undefined" || !document.fonts) {
        this.symbolFontState = "unavailable";
        return;
      }
      new FontFace(SYMBOL_FAMILY, `url(${this.state.config.symbols.url})`).load().then(
        (loaded) => {
          document.fonts.add(loaded);
          this.symbolFontState = "ok";
          if (typeof this.drawPreview === "function") this.drawPreview();
        },
        () => {
          this.symbolFontState = "unavailable";
        },
      );
    }

    /* A run of symbols' width at sizePx in the symbol font. */
    symbolAdvance(ctx, text, sizePx) {
      const font = ctx.font;
      ctx.font = `${sizePx}px ${SYMBOL_FAMILY}`;
      const width = ctx.measureText(text).width;
      ctx.font = font;
      return width;
    }

    emojiArtState(key) {
      const entry = this.emojiArt && this.emojiArt.get(key);
      return entry ? entry.state : "unknown";
    }

    /* Loads one emoji's artwork from the app. A 404 means the print can't
       draw it ("missing", refused). Any other failure leaves it "unavailable":
       not drawn here, and the app's check decides at save. The SVG carries a
       viewBox only, so it is given a size before it becomes an image: Firefox
       won't draw an SVG without one onto a canvas. */
    requestEmojiArt(key) {
      if (!this.emojiArt) this.emojiArt = new Map();
      if (this.emojiArt.has(key)) return;
      const entry = { state: "loading", img: null, shadow: null };
      this.emojiArt.set(key, entry);
      const settle = (state) => {
        entry.state = state;
        if (typeof this.drawPreview === "function") this.drawPreview();
        this.refreshValidity();
      };
      if (typeof fetch !== "function" || typeof Image === "undefined") { entry.state = "unavailable"; return; }
      fetch(`${this.state.config.emoji.url}${key}.svg`)
        .then((res) => {
          if (res.status === 404) return settle("missing");
          if (!res.ok) return settle("unavailable");
          return res.text().then((svg) => {
            const sized = /<svg[^>]*\swidth=/.test(svg) ? svg : svg.replace("<svg", '<svg width="128" height="128"');
            const url = URL.createObjectURL(new Blob([sized], { type: "image/svg+xml" }));
            const img = new Image();
            img.onload = () => { entry.img = img; settle("ok"); };
            img.onerror = () => settle("unavailable");
            img.src = url;
          });
        })
        .catch(() => settle("unavailable"));
    }

    /* The emoji's picture in black, for the overlay's shadow: the words'
       shadow is the words in black at 35%, and an emoji's is its shape. */
    emojiShadow(entry) {
      if (entry.shadow || typeof document === "undefined") return entry.shadow;
      const c = document.createElement("canvas");
      c.width = 128;
      c.height = 128;
      const g = c.getContext("2d");
      g.drawImage(entry.img, 0, 0, 128, 128);
      g.globalCompositeOperation = "source-in";
      g.fillStyle = "#000000";
      g.fillRect(0, 0, 128, 128);
      entry.shadow = c;
      return c;
    }

    /* A line's width at the size ctx.font is set to (sizePx). A line of the
       font's own text is measureText exactly as before; each emoji counts
       advanceEm, a symbol run its width in the symbol font, and a refused
       character nothing. */
    lineAdvance(ctx, line, sizePx) {
      if (!this.v35) return ctx.measureText(line).width;
      const split = this.splitWriting(line);
      if (!this.isPiecewise(split)) return ctx.measureText(line).width;
      const advance = this.state.config.emoji.advanceEm * sizePx;
      return split.pieces.reduce((sum, p) => {
        if (p.kind === "emoji") return sum + advance;
        if (p.kind === "symbol") return sum + this.symbolAdvance(ctx, p.text, sizePx);
        return sum + ctx.measureText(p.text).width;
      }, 0);
    }

    /* Draws one line centred on cx, the way the print does: the words with
       fillText from the left of the line, each run of symbols in the symbol
       font in the same paint, each emoji as its artwork sizeEm square,
       centred in its advanceEm slot on the line's centre, and nothing for a
       refused character. Returns false for a line of the font's own text,
       which the caller draws as before. */
    drawWritingLine(ctx, line, cx, y, fontSize, shadow) {
      if (!this.v35) return false;
      const split = this.splitWriting(line);
      if (!this.isPiecewise(split)) return false;
      const { pieces } = split;
      const { sizeEm, advanceEm } = this.state.config.emoji;
      const advance = advanceEm * fontSize;
      const box = sizeEm * fontSize;
      let x = cx - this.lineAdvance(ctx, line, fontSize) / 2;
      const align = ctx.textAlign;
      ctx.textAlign = "left";
      for (const piece of pieces) {
        if (piece.kind === "text") {
          ctx.fillText(piece.text, x, y);
          x += ctx.measureText(piece.text).width;
          continue;
        }
        if (piece.kind === "symbol") {
          const width = this.symbolAdvance(ctx, piece.text, fontSize);
          if (this.symbolFontState === "ok") {
            const font = ctx.font;
            ctx.font = `${fontSize}px ${SYMBOL_FAMILY}`;
            ctx.fillText(piece.text, x, y);
            ctx.font = font;
          }
          x += width;
          continue;
        }
        const entry = this.emojiArt && this.emojiArt.get(piece.key);
        if (entry && entry.state === "ok") {
          const picture = shadow ? this.emojiShadow(entry) : entry.img;
          if (picture) {
            const alpha = ctx.globalAlpha;
            if (shadow) ctx.globalAlpha = alpha * 0.35;
            ctx.drawImage(picture, x + (advance - box) / 2, y - box / 2, box, box);
            ctx.globalAlpha = alpha;
          }
        }
        x += advance;
      }
      ctx.textAlign = align;
      return true;
    }

    buildPhotoRow() {
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      const label = document.createElement("label");
      label.className = "tfk-customiser__label";
      label.htmlFor = "tfk-customiser-photo";
      label.textContent = "Your photo";
      const input = document.createElement("input");
      input.type = "file";
      input.id = "tfk-customiser-photo";
      input.accept = "image/jpeg,image/png,image/webp"; // contract: no HEIC. iPhone Safari re-encodes to JPEG
      input.addEventListener("change", () => {
        if (input.files && input.files[0]) this.handlePhotoSelected(input.files[0]);
      });
      this.photoStatus = document.createElement("p");
      this.photoStatus.className = "tfk-customiser__note";
      if (this.state.photo && this.state.photo.remote) {
        this.photoStatus.textContent = "Your photo is on file. Upload a new one to replace it.";
      }
      row.append(label, input, this.photoStatus);
      return row;
    }

    /* Minimum crop width in source pixels for acceptable print density.
       Falls back to 72 dpi if the config ever omits minEffectiveDpi, so the
       low-resolution warning can never silently disappear. */
    minPrintWidthPx() {
      const f = this.state.config.format;
      const dpi = typeof f.minEffectiveDpi === "number" && f.minEffectiveDpi > 0 ? f.minEffectiveDpi : 72;
      // v3.0: evaluated against the rotated width analogue when portrait.
      return (this.sheetWMm() / 25.4) * dpi;
    }

    async handlePhotoSelected(file) {
      const MAX = 20 * 1024 * 1024;
      this.errorSlot.innerHTML = "";
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        this.errorSlot.appendChild(this.buildError("Please choose a JPEG, PNG or WebP image."));
        return;
      }
      if (file.size > MAX) {
        this.errorSlot.appendChild(this.buildError("That image is over 20 MB. Please choose a smaller one."));
        return;
      }
      let bitmap;
      try {
        // EXIF-upright by default in modern browsers. Matches the server's
        // rotate-before-crop, so crop coordinates line up (contract).
        bitmap = await createImageBitmap(file);
      } catch {
        this.errorSlot.appendChild(this.buildError("That image could not be read. Please try a different photo."));
        return;
      }
      this.state.photo = { file, bitmap, key: null, previewUrl: null, uploadState: "uploading" };
      const photo = this.state.photo;
      // Contract v3.0 convenience: a tall upload defaults the design to
      // portrait, one-way, unless the customer has explicitly toggled.
      if (this.v30 && !this.orientationTouched && bitmap.height > bitmap.width) {
        this.setOrientation("portrait");
      }
      this.state.pan = { x: 0.5, y: 0.5 };
      this.state.zoom = 1;
      if (this.zoomSlider) this.zoomSlider.value = "100";
      this.state.lowResAccepted = false; // fresh photo, fresh acknowledgement
      // Force the warning surfaces to reset even when the previous photo was
      // also low-res (no state transition otherwise, which would leave a
      // stale ticked checkbox over the reset state).
      this.lowResActive = false;
      if (this.resolutionSlot) this.resolutionSlot.innerHTML = "";
      if (this.lowResRow) this.lowResRow.hidden = true;
      if (this.lowResBox) this.lowResBox.checked = false;
      this.photoStatus.textContent = "Uploading photo…";
      this.refreshValidity();
      this.drawPreview();
      try {
        const presign = await fetch(`${API_BASE}/upload-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contentType: file.type, sizeBytes: file.size }),
        });
        if (!presign.ok) throw new Error(`presign ${presign.status}`);
        const { key, uploadUrl, previewUrl } = await presign.json();
        if (this.state.photo !== photo) return;
        const put = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
        if (!put.ok) throw new Error(`upload ${put.status}`);
        if (this.state.photo !== photo) return;
        photo.key = key;
        photo.previewUrl = previewUrl;
        photo.uploadState = "done";
        this.photoStatus.textContent = "Photo uploaded.";
      } catch (err) {
        if (this.state.photo !== photo) return;
        console.warn("[customiser] upload failed", err);
        photo.uploadState = "error";
        this.photoStatus.textContent = "";
        this.errorSlot.appendChild(this.buildError("Photo upload failed. Please try again."));
      }
      if (this.state.photo !== photo) return;
      this.refreshValidity();
      this.drawPreview();
    }

    /* ---- contract v2.7: cupcake multi-image disc allocation ---- */

    isPhotoOnly() {
      const f = this.state.config && this.state.config.format;
      return !!(f && f.supportsPhoto && !f.supportsMessage);
    }

    isCupcakeAllocator() {
      return !!(this.v27 && this.isPhotoOnly());
    }

    /* pool = packSize x native quantity; null when packSize is unavailable. */
    poolSize() {
      return this.packSize ? this.packSize * this.nativeQty() : null;
    }

    allocatedCount() {
      return this.state.images.reduce((sum, im) => sum + (im.count || 0), 0);
    }

    buildAllocatorRow() {
      const cfg = this.state.config;
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      const label = document.createElement("span");
      label.className = "tfk-customiser__label";
      label.textContent = "Your cupcake photos";
      const help = document.createElement("p");
      help.className = "tfk-customiser__note";
      help.textContent = "Add up to " + cfg.format.maxImages + " photos and choose how many cupcakes each one prints on.";
      this.slotsEl = document.createElement("div");
      this.slotsEl.className = "tfk-customiser__slots";
      // "Add photo" is a file input styled as a button; hidden once maxImages reached.
      const addWrap = document.createElement("label");
      addWrap.className = "tfk-customiser__add-image";
      addWrap.append(document.createTextNode("+ Add photo"));
      const addInput = document.createElement("input");
      addInput.type = "file";
      addInput.accept = "image/jpeg,image/png,image/webp"; // contract: no HEIC
      addInput.hidden = true;
      addInput.addEventListener("change", () => {
        if (addInput.files && addInput.files[0]) this.addImageSlot(addInput.files[0]);
        addInput.value = ""; // allow re-picking the same file
      });
      addWrap.appendChild(addInput);
      this.addImageWrap = addWrap;
      this.readoutEl = document.createElement("p");
      this.readoutEl.className = "tfk-customiser__readout";
      row.append(label, help, this.slotsEl, addWrap, this.readoutEl);
      this.renderSlots();
      this.updateAllocationReadout();
      return row;
    }

    renderSlots() {
      if (!this.slotsEl) return;
      this.slotsEl.innerHTML = "";
      this.state.images.forEach((slot, i) => this.slotsEl.appendChild(this.buildImageSlot(slot, i)));
      this.updateAddVisibility();
    }

    updateAddVisibility() {
      if (!this.addImageWrap) return;
      this.addImageWrap.hidden = this.state.images.length >= this.state.config.format.maxImages;
    }

    buildImageSlot(slot, index) {
      const card = document.createElement("div");
      card.className = "tfk-customiser__slot";
      const title = document.createElement("span");
      title.className = "tfk-customiser__slot-title";
      title.textContent = "Photo " + (index + 1);
      const canvas = document.createElement("canvas");
      canvas.className = "tfk-customiser__slot-canvas";
      canvas.width = 300;
      canvas.height = 300;
      slot.canvas = canvas;
      const zoom = document.createElement("input");
      zoom.type = "range";
      zoom.className = "tfk-customiser__zoom";
      zoom.min = "100";
      zoom.max = "300";
      zoom.value = String(Math.round(slot.zoom * 100));
      zoom.setAttribute("aria-label", "Zoom photo " + (index + 1));
      zoom.addEventListener("input", () => {
        slot.zoom = Number(zoom.value) / 100;
        this.clampPanForSlot(slot);
        this.drawSlot(slot);
      });
      zoom.hidden = !!slot.remote; // remote slots keep their stored crop; nothing to zoom
      const status = document.createElement("p");
      status.className = "tfk-customiser__note";
      status.textContent = slot.remote
        ? "Photo on file."
        : slot.uploadState === "done" ? "Photo added." : slot.uploadState === "error" ? "" : "Uploading photo…";
      slot.statusEl = status;
      const stepper = this.buildStepper(slot);
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "tfk-customiser__slot-remove";
      remove.textContent = "Remove";
      remove.addEventListener("click", () => this.removeImageSlot(slot));
      card.append(title, canvas, zoom, status, stepper, remove);
      this.bindPanForSlot(slot, canvas);
      this.drawSlot(slot);
      return card;
    }

    buildStepper(slot) {
      const wrap = document.createElement("div");
      wrap.className = "tfk-customiser__stepper";
      const cap = document.createElement("span");
      cap.className = "tfk-customiser__stepper-label";
      cap.textContent = "Cupcakes:";
      const minus = document.createElement("button");
      minus.type = "button";
      minus.className = "tfk-customiser__stepper-btn";
      minus.textContent = "−"; // minus sign
      minus.setAttribute("aria-label", "Fewer cupcakes");
      minus.addEventListener("click", () => this.setSlotCount(slot, slot.count - 1));
      const input = document.createElement("input");
      input.type = "number";
      input.className = "tfk-customiser__count-input";
      input.min = "1";
      input.inputMode = "numeric";
      input.value = String(slot.count);
      input.addEventListener("input", () => this.setSlotCount(slot, parseInt(input.value, 10), true));
      const plus = document.createElement("button");
      plus.type = "button";
      plus.className = "tfk-customiser__stepper-btn";
      plus.textContent = "+";
      plus.setAttribute("aria-label", "More cupcakes");
      plus.addEventListener("click", () => this.setSlotCount(slot, slot.count + 1));
      slot.countInput = input;
      wrap.append(cap, minus, input, plus);
      return wrap;
    }

    /* count >= 1 (remove the image to opt it out, never 0); hard-capped at the
       server's 500 total ceiling. fromInput leaves the field alone mid-typing. */
    setSlotCount(slot, next, fromInput) {
      let n = Math.floor(next);
      if (!Number.isInteger(n) || n < 1) n = 1;
      if (n > 500) n = 500;
      slot.count = n;
      if (slot.countInput && (!fromInput || slot.countInput.value !== String(n))) slot.countInput.value = String(n);
      this.updateAllocationReadout();
      this.refreshValidity();
    }

    async addImageSlot(file) {
      const cfg = this.state.config;
      if (this.state.images.length >= cfg.format.maxImages) return;
      const MAX = 20 * 1024 * 1024;
      this.errorSlot.innerHTML = "";
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        this.errorSlot.appendChild(this.buildError("Please choose a JPEG, PNG or WebP image."));
        return;
      }
      if (file.size > MAX) {
        this.errorSlot.appendChild(this.buildError("That image is over 20 MB. Please choose a smaller one."));
        return;
      }
      let bitmap;
      try {
        bitmap = await createImageBitmap(file); // EXIF-upright, matches server
      } catch {
        this.errorSlot.appendChild(this.buildError("That image could not be read. Please try a different photo."));
        return;
      }
      // New image fills the remaining pool (clamped >= 1), so a single photo
      // covers the whole pack; adding more, the customer rebalances from there.
      const pool = this.poolSize();
      const remaining = pool != null ? pool - this.allocatedCount() : 1;
      const slot = {
        id: ++this._slotSeq,
        file,
        bitmap,
        key: null,
        previewUrl: null,
        uploadState: "uploading",
        pan: { x: 0.5, y: 0.5 },
        zoom: 1,
        count: Math.max(1, remaining),
      };
      this.state.images.push(slot);
      this.renderSlots();
      this.updateAllocationReadout();
      this.refreshValidity();
      this.uploadSlotPhoto(slot);
    }

    async uploadSlotPhoto(slot) {
      const file = slot.file;
      try {
        const presign = await fetch(`${API_BASE}/upload-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contentType: file.type, sizeBytes: file.size }),
        });
        if (!presign.ok) throw new Error(`presign ${presign.status}`);
        const { key, uploadUrl, previewUrl } = await presign.json();
        if (!this.state.images.includes(slot)) return; // removed mid-flight
        const put = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
        if (!put.ok) throw new Error(`upload ${put.status}`);
        if (!this.state.images.includes(slot)) return;
        slot.key = key;
        slot.previewUrl = previewUrl;
        slot.uploadState = "done";
        if (slot.statusEl) slot.statusEl.textContent = "Photo added.";
      } catch (err) {
        if (!this.state.images.includes(slot)) return;
        console.warn("[customiser] cupcake upload failed", err);
        slot.uploadState = "error";
        if (slot.statusEl) slot.statusEl.textContent = "";
        this.errorSlot.appendChild(this.buildError("A photo failed to upload. Please remove it and try again."));
      }
      if (!this.state.images.includes(slot)) return;
      this.refreshValidity();
    }

    removeImageSlot(slot) {
      const i = this.state.images.indexOf(slot);
      if (i === -1) return;
      this.state.images.splice(i, 1);
      this.errorSlot.innerHTML = ""; // drop any upload-error text for the removed slot
      this.renderSlots();
      this.updateAllocationReadout();
      this.refreshValidity();
    }

    updateAllocationReadout() {
      if (!this.readoutEl) return;
      this.updateAddVisibility();
      const pool = this.poolSize();
      if (pool == null) {
        this.readoutEl.textContent = "Pack size is unavailable for this product. Please contact us before ordering.";
        this.readoutEl.classList.add("is-blocked");
        return;
      }
      const allocated = this.allocatedCount();
      const remaining = pool - allocated;
      let text = allocated + " of " + pool + " cupcakes allocated";
      if (remaining > 0) text += " · " + remaining + " remaining";
      else if (remaining < 0) text += " · " + (-remaining) + " over";
      else text += " · all allocated";
      this.readoutEl.textContent = text;
      this.readoutEl.classList.toggle("is-blocked", remaining !== 0);
    }

    /* Crop dimensions in source pixels (contract v2.7 disc-framing update). The
       server now CONTAINS the crop on a white disc, so the crop is no longer
       forced square. Three anchors across the zoom slider (z = 1..3):
         z = 1 (min):   the WHOLE image (w x h); server letterboxes it white.
         z = 2 (cover): the largest centred square, side = min(w,h), fills the
                        disc edge-to-edge.
         z = 3 (max):   min(w,h)/3 square, the pre-update tightest crop, unchanged.
       Phase z <= 2 shrinks only the long axis (short axis stays full), so the
       crop is non-square there (that is expected); phase z > 2 shrinks the square. */
    slotCropDims(slot) {
      const bmp = slot.bitmap;
      const w = bmp.width;
      const h = bmp.height;
      const short = Math.min(w, h);
      const long = Math.max(w, h);
      const z = slot.zoom;
      let shortLen;
      let longLen;
      if (z <= 2) {
        shortLen = short;
        longLen = long - (long - short) * (z - 1);
      } else {
        shortLen = short / (2 * z - 3);
        longLen = shortLen;
      }
      return w >= h ? { cropW: longLen, cropH: shortLen } : { cropW: shortLen, cropH: longLen };
    }

    /* Integer crop rect, always in-bounds: 0 <= x, y and x + width <= w,
       y + height <= h, positive dimensions (never an oversized/negative crop the
       server would reject). May be non-square; the server contains it on white. */
    computeCropForSlot(slot) {
      // A remote slot passes its stored crop through untouched. Keyed on `remote`
      // as well as the bitmap: since C40 a stored slot is decoded so its disc can
      // draw, and its crop must still not be recomputed from the default pan/zoom.
      if (slot.remote || !slot.bitmap) return slot.crop;
      const bmp = slot.bitmap;
      const dims = this.slotCropDims(slot);
      const cw = Math.max(1, Math.min(bmp.width, Math.round(dims.cropW)));
      const ch = Math.max(1, Math.min(bmp.height, Math.round(dims.cropH)));
      const x = Math.round(slot.pan.x * bmp.width - cw / 2);
      const y = Math.round(slot.pan.y * bmp.height - ch / 2);
      return {
        x: Math.max(0, Math.min(bmp.width - cw, x)),
        y: Math.max(0, Math.min(bmp.height - ch, y)),
        width: cw,
        height: ch,
      };
    }

    clampPanForSlot(slot) {
      if (!slot.bitmap || slot.remote) return;
      const bmp = slot.bitmap;
      const dims = this.slotCropDims(slot);
      const hx = Math.min(0.5, dims.cropW / bmp.width / 2);
      const hy = Math.min(0.5, dims.cropH / bmp.height / 2);
      slot.pan.x = Math.min(1 - hx, Math.max(hx, slot.pan.x));
      slot.pan.y = Math.min(1 - hy, Math.max(hy, slot.pan.y));
    }

    bindPanForSlot(slot, canvas) {
      let dragging = false;
      let last = null;
      canvas.addEventListener("pointerdown", (e) => {
        if (!slot.bitmap || slot.remote) return; // a stored slot keeps its framing
        dragging = true;
        last = { x: e.clientX, y: e.clientY };
        canvas.setPointerCapture(e.pointerId);
      });
      canvas.addEventListener("pointermove", (e) => {
        if (!dragging) return;
        const scale = canvas.getBoundingClientRect().width / canvas.width;
        const crop = this.computeCropForSlot(slot);
        const bmp = slot.bitmap;
        // canvas-pixel drag -> source-fraction pan (inverted: drag right shows more left)
        slot.pan.x -= ((e.clientX - last.x) / scale / canvas.width) * (crop.width / bmp.width);
        slot.pan.y -= ((e.clientY - last.y) / scale / canvas.height) * (crop.height / bmp.height);
        last = { x: e.clientX, y: e.clientY };
        this.clampPanForSlot(slot);
        this.drawSlot(slot);
      });
      ["pointerup", "pointercancel"].forEach((t) => canvas.addEventListener(t, () => { dragging = false; }));
    }

    /* Round-disc preview (contract v2.7, disc-framing update): a WHITE disc with
       the crop CONTAINED (letterboxed white where the aspect does not fill),
       matching the server's contain-on-white render exactly. Preview-only; the
       crop sent is unchanged. Transparent PNG areas read white because the disc
       is filled white before the image is drawn. */
    drawSlot(slot) {
      const canvas = slot.canvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const size = canvas.width;
      ctx.clearRect(0, 0, size, size);
      ctx.save();
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.fillStyle = "#ffffff"; // white icing sheet; also backs transparent PNG areas
      ctx.fillRect(0, 0, size, size);
      if (slot.bitmap) {
        const crop = this.computeCropForSlot(slot);
        const scale = Math.min(size / crop.width, size / crop.height);
        const dw = crop.width * scale;
        const dh = crop.height * scale;
        ctx.drawImage(slot.bitmap, crop.x, crop.y, crop.width, crop.height, (size - dw) / 2, (size - dh) / 2, dw, dh);
      } else if (slot.remote) {
        ctx.fillStyle = "#999999"; /* placeholder text on the white disc, dynamic canvas paint */
        ctx.font = "28px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Photo on file", size / 2, size / 2);
      }
      ctx.restore();
    }

    buildPreviewRow() {
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      const label = document.createElement("span");
      label.className = "tfk-customiser__label";
      label.textContent = "Preview";
      this.canvas = document.createElement("canvas");
      this.canvas.className = "tfk-customiser__preview";
      this.canvas.width = 960; // internal resolution; CSS scales down
      this.canvas.height = 720;
      this.zoomSlider = document.createElement("input");
      this.zoomSlider.type = "range";
      this.zoomSlider.className = "tfk-customiser__zoom";
      // v3.2: the floor is set per upload by refreshZoomRange (the whole-image
      // fit for this design and this photo). 100 until a photo arrives, and
      // 100 stays the DEFAULT value in every case — the fill point does not
      // move, so a customer who never drags sees exactly what they saw before.
      this.zoomSlider.min = "100";
      this.zoomSlider.max = "300";
      this.zoomSlider.value = "100";
      this.zoomSlider.setAttribute("aria-label", "Zoom photo");
      this.zoomSlider.addEventListener("input", () => {
        const toZoom = Number(this.zoomSlider.value) / 100;
        const bmp = this.state.photo && this.state.photo.bitmap;
        if (bmp) {
          // C26 fix 3: zooming out keeps the framing's place in the range
          // rather than letting clampPan pin it to an edge.
          const area = this.photoAreaCanvasRect();
          this.state.pan = TfkCakeGeometry.panForZoom(bmp.width, bmp.height, area.w, area.h, this.state.zoom, toZoom, this.state.pan);
        }
        this.state.zoom = toZoom;
        this.clampPan();
        this.drawPreview();
      });
      // v3.2: with the default unchanged, dragging is the only way to find the
      // fit, so the control says so. Hidden until there is a photo to fit.
      this.zoomHelp = document.createElement("p");
      this.zoomHelp.className = "tfk-customiser__note tfk-customiser__zoom-help";
      this.zoomHelp.textContent = "Drag left to fit your whole photo on the cake. Any space around it prints white.";
      this.zoomHelp.hidden = true;
      this.zoomControl = document.createElement("div");
      this.zoomControl.className = "tfk-customiser__zoom-control";
      this.zoomControl.append(this.zoomSlider, this.zoomHelp);
      this.resolutionSlot = document.createElement("div");
      this.lowResActive = false; // fresh slot is empty; kept in sync by updateResolutionWarning
      // C26: advisory only, no acknowledgement and no block on Add to cart.
      this.cropWarningSlot = document.createElement("div");
      this.cropWarningSlot.setAttribute("aria-live", "polite");
      this.cutEdgesKey = ""; // fresh slot is empty; kept in sync by updateCropWarning
      // v3.0: the zoom bar and the orientation icon pair share one controls
      // line under the canvas, each in a labelled group box; the orientation
      // group only exists when the gate passes.
      const controls = document.createElement("div");
      controls.className = "tfk-customiser__preview-controls";
      if (this.isCelebration()) {
        // C44: a Celebration cake has no photo, so the photo zoom did nothing
        // there. It gets a Text size slider instead, from contract 3.3, and no
        // control at all on an older backend, which would print the fitted size
        // whatever the slider said (ruled 29 September 2026).
        this.zoomSlider = null;
        this.zoomHelp = null;
        if (this.v33) controls.appendChild(this.buildControlGroup("Text size", this.buildTextSizeControl(), "zoom"));
      } else {
        controls.appendChild(this.buildControlGroup("Zoom", this.zoomControl, "zoom"));
      }
      if (this.v30 && !this.isCupcakeAllocator()) {
        controls.appendChild(this.buildControlGroup("Orientation", this.buildOrientationControl(), "orientation"));
      }
      row.append(label, this.canvas, controls, this.cropWarningSlot, this.resolutionSlot);
      this.bindPan();
      this.bindCalibration();
      this.setMockupForVariant(this.currentVariantId);
      this.drawPreview();
      return row;
    }

    /* Dev-only helper for the rare area override: with ?tfkCalibrate=1, click
       two opposite corners of the painted blank zone (the INNER rect under
       the 2026-07-15 mockup spec) on the preview; the helper expands by the
       margin fractions and logs the sheet-rect metafield string to the
       console. No effect for customers. */
    bindCalibration() {
      if (!/[?&]tfkCalibrate=1/.test(window.location.search)) return;
      let corners = [];
      this.canvas.addEventListener("click", (e) => {
        // v3.0: the metafield is declared in LANDSCAPE image fractions; the
        // rotated preview would log a rotated rect. Calibrate in landscape.
        if (this.isPortrait()) {
          console.warn("[customiser] calibrate: select the landscape icon first — the area metafield is declared in landscape fractions");
          return;
        }
        const imgRect = this.mockupCanvasRect();
        if (!imgRect) return;
        const box = this.canvas.getBoundingClientRect();
        const px = ((e.clientX - box.left) / box.width) * this.canvas.width;
        const py = ((e.clientY - box.top) / box.height) * this.canvas.height;
        corners.push({ x: (px - imgRect.x) / imgRect.w, y: (py - imgRect.y) / imgRect.h });
        if (corners.length < 2) {
          console.log("[customiser] calibrate: corner 1 recorded, now click the opposite corner");
          return;
        }
        const [a, b] = corners;
        corners = [];
        let x = Math.min(a.x, b.x);
        let y = Math.min(a.y, b.y);
        let w = Math.abs(a.x - b.x);
        let h = Math.abs(a.y - b.y);
        // Mockup spec rev 2026-07-15: the painted blank zone (what gets
        // clicked) is the INNER rect, while the metafield declares the SHEET
        // rect, so expand outward by the margin fractions when the config
        // carries them. Pre-v2.4 configs lack marginMm; clicks then declare
        // the sheet directly, matching the old spec.
        const f = this.state.config && this.state.config.format;
        const mw = this.v24 ? f.marginMm / f.sheetWidthMm : 0;
        const mh = this.v24 ? f.marginMm / f.sheetHeightMm : 0;
        if (mw > 0 && mw < 0.5 && mh > 0 && mh < 0.5) {
          w = w / (1 - 2 * mw);
          h = h / (1 - 2 * mh);
          x = x - w * mw;
          y = y - h * mh;
          console.log("[customiser] calibrate: clicked inner zone expanded by marginMm to the sheet rect");
        }
        if (x < 0 || y < 0 || x + w > 1 || y + h > 1) {
          console.warn("[customiser] calibrate: the expanded sheet rect spills past the image edge; mockupAreaOverride() will reject this value. The photo needs more border around the painted zone.");
        }
        const vals = [x, y, w, h];
        console.log("[customiser] customiser_mockup_area = " + vals.map((n) => n.toFixed(3)).join(","));
      });
    }

    /* Contract v2: mockups resolve variant metafield, then product fallback,
       then flat sheet. Shopify CDN URLs are public and permanent, no expiry. */
    setMockupForVariant(variantId) {
      const url = (variantId != null && this.mockups.byVariant[String(variantId)]) || this.mockups.fallback || null;
      if (!url) {
        this.mockupImage = null;
        this.drawPreview();
        return;
      }
      if (this.mockupImage && this.mockupImage.src === url) return; // same photo, keep it
      const img = new Image();
      img.crossOrigin = "anonymous"; // Shopify CDN sends CORS headers; keeps the canvas untainted
      img.onload = () => this.drawPreview();
      img.onerror = () => {
        // A 404/blocked/misreferenced mockup URL must not degrade silently
        // (2026-07-15 app team request). Guard against a superseded load: a
        // later variant switch may already have replaced this.mockupImage.
        console.warn("[customiser] mockup image failed to load, falling back to flat sheet:", url);
        if (this.mockupImage === img) {
          this.mockupImage = null;
          this.drawPreview();
        }
      };
      this.mockupImage = img;
      img.src = url;
      this.drawPreview();
    }

    bindPan() {
      let dragMode = null; // "text" | "photo" | null
      let last = null;
      this.canvas.addEventListener("pointerdown", (e) => {
        // Contract v2.4: hit-test the overlay text box first, so a
        // message-only overlay design (no photo) is still draggable. The
        // pre-v2.4 early return (no photo -> ignore) moves to after this
        // hit-test; lastTextBox is always null when !v24, so the behaviour
        // for that path is unchanged.
        const box = this.canvas.getBoundingClientRect();
        const scale = box.width / this.canvas.width;
        const px = (e.clientX - box.left) / scale;
        const py = (e.clientY - box.top) / scale;
        const tb = this.lastTextBox;
        if (tb && px >= tb.x && px <= tb.x + tb.w && py >= tb.y && py <= tb.y + tb.h) {
          dragMode = "text";
        } else if (this.state.photo) {
          dragMode = "photo";
        } else {
          return;
        }
        last = { x: e.clientX, y: e.clientY };
        this.canvas.setPointerCapture(e.pointerId);
      });
      this.canvas.addEventListener("pointermove", (e) => {
        if (dragMode === "text") {
          const sheet = this.sheetCanvasRect();
          const scale = this.canvas.getBoundingClientRect().width / this.canvas.width;
          this.state.messageAnchor.x += (e.clientX - last.x) / scale / sheet.w;
          this.state.messageAnchor.y += (e.clientY - last.y) / scale / sheet.h;
          this.state.messageAnchor.x = Math.min(1, Math.max(0, this.state.messageAnchor.x));
          this.state.messageAnchor.y = Math.min(1, Math.max(0, this.state.messageAnchor.y));
          last = { x: e.clientX, y: e.clientY };
          this.drawPreview();
          return;
        }
        if (dragMode !== "photo" || !this.state.photo) return;
        const area = this.photoAreaCanvasRect();
        // convert canvas-pixel drag to source-fraction pan (inverted: drag right shows more left)
        const crop = this.computeCrop();
        if (!crop) return;
        const scale = this.canvas.getBoundingClientRect().width / this.canvas.width;
        this.state.pan.x -= ((e.clientX - last.x) / scale / area.w) * (crop.width / this.state.photo.bitmap.width);
        this.state.pan.y -= ((e.clientY - last.y) / scale / area.h) * (crop.height / this.state.photo.bitmap.height);
        last = { x: e.clientX, y: e.clientY };
        this.clampPan();
        this.drawPreview();
      });
      ["pointerup", "pointercancel"].forEach((t) => this.canvas.addEventListener(t, () => { dragMode = null; }));
    }

    /* The drawable mockup and its dimensions. Portrait (contract v3.0,
       build-note pinned detail A): the bitmap rotates 90° CLOCKWISE — the
       same direction the server's emailed on-cake composite uses, so the
       two never mirror each other. Rotated once into an offscreen canvas,
       cached per source URL; the crossOrigin="anonymous" load keeps both
       canvases untainted. */
    mockupSource() {
      const img = this.mockupImage;
      if (!(img && img.complete && img.naturalWidth > 0)) return null;
      if (!this.isPortrait()) return { src: img, w: img.naturalWidth, h: img.naturalHeight };
      if (this._rotatedMockupFor !== img.src) {
        const c = document.createElement("canvas");
        c.width = img.naturalHeight;
        c.height = img.naturalWidth;
        const cx = c.getContext("2d");
        cx.translate(c.width, 0);
        cx.rotate(Math.PI / 2);
        cx.drawImage(img, 0, 0);
        this._rotatedMockup = c;
        this._rotatedMockupFor = img.src;
      }
      return { src: this._rotatedMockup, w: this._rotatedMockup.width, h: this._rotatedMockup.height };
    }

    /* Contain-fit rect of the loaded mockup at natural aspect (rotated when
       portrait), scaled by the display zoom (still centred, so it crops only
       the photo's background border; may extend past the canvas). Null
       without a mockup. Downstream geometry (canonical sheet, area override,
       calibration clicks) works in fractions of this rect, so everything
       follows the zoom automatically. */
    mockupCanvasRect() {
      const src = this.mockupSource();
      if (!src) return null;
      const scale = Math.min(this.canvas.width / src.w, this.canvas.height / src.h)
        * MOCKUP_DISPLAY_ZOOM;
      const w = src.w * scale;
      const h = src.h * scale;
      return { x: (this.canvas.width - w) / 2, y: (this.canvas.height - h) / 2, w, h };
    }

    /* Rare override (contract v2.2): "x,y,w,h" fractions of the natural image,
       top-left origin, variant then product resolution. Malformed → null. */
    mockupAreaOverride() {
      const raw = (this.currentVariantId != null && this.mockups.areaByVariant[String(this.currentVariantId)])
        || this.mockups.areaFallback || null;
      if (typeof raw !== "string") return null;
      const parts = raw.split(",").map(Number);
      if (parts.length !== 4 || parts.some((n) => !isFinite(n))) return null;
      const [x, y, w, h] = parts;
      if (w <= 0 || h <= 0 || x < 0 || y < 0 || x + w > 1 || y + h > 1) return null;
      if (!this.isPortrait()) return { x, y, w, h };
      // Contract v3.0 pinned transform: the override follows the bitmap's
      // 90° CW rotation (x' = 1 − y − h, y' = x, w/h swapped). A valid rect
      // stays inside the unit square, so no re-validation is needed.
      return { x: 1 - y - h, y: x, w: h, h: w };
    }

    /* Contain-fit the format-aspect sheet into a bounding box, centred.
       The override may move or scale the sheet, never reshape it. At the
       v2.3 canonical 52% width every live format fits a 4:3 base unclamped;
       the height clamp remains as a guard for extreme override boxes. */
    fitSheetIntoBox(box) {
      const aspect = this.sheetWMm() / this.sheetHMm(); // v3.0: rotated aspect when portrait
      let w = box.w;
      let h = w / aspect;
      if (h > box.h) { h = box.h; w = h * aspect; }
      return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
    }

    /* Sheet geometry (contract v2.3 canonical framing): format-aspect sheet at
       52% of the base width, centred both axes, real cake at true proportions
       around it. Base = drawn mockup rect when a photo is present, else the
       canvas scaled by the same display zoom. A conforming 4:3 mockup
       contain-fits to exactly the canvas rect, so both bases zoom identically
       and the sheet never moves or resizes across flavour swaps, mockup load
       pop-in, or the flat-sheet fallback. */
    sheetCanvasRect() {
      const imgRect = this.mockupCanvasRect();
      const zw = this.canvas.width * MOCKUP_DISPLAY_ZOOM;
      const zh = this.canvas.height * MOCKUP_DISPLAY_ZOOM;
      const base = imgRect || {
        x: (this.canvas.width - zw) / 2,
        y: (this.canvas.height - zh) / 2,
        w: zw,
        h: zh,
      };
      const area = imgRect ? this.mockupAreaOverride() : null;
      if (area) {
        return this.fitSheetIntoBox({
          x: base.x + area.x * base.w,
          y: base.y + area.y * base.h,
          w: area.w * base.w,
          h: area.h * base.h,
        });
      }
      return this.fitSheetIntoBox({ x: base.x + base.w * 0.24, y: base.y, w: base.w * 0.52, h: base.h });
    }

    /* Contract v2.4.1: sheet minus marginMm on all four sides, in preview
       canvas px. When the backend does not support v2.4 (or does not carry
       marginMm/dpi), returns the sheet unchanged so every existing geometry
       call stays byte-identical to pre-v2.4 behaviour. */
    innerCanvasRect() {
      const sheet = this.sheetCanvasRect();
      if (!this.v24) return sheet;
      const pxPerMm = sheet.w / this.sheetWMm();
      const inset = this.state.config.format.marginMm * pxPerMm;
      return { x: sheet.x + inset, y: sheet.y + inset, w: sheet.w - 2 * inset, h: sheet.h - 2 * inset };
    }

    /* Photo area within the inner (margin-inset) rect: mirrors server layout,
       22% bottom text band when a message renders in the band. In overlay
       placement (v2.4) the photo covers the full inner rect, no band. */
    photoAreaCanvasRect() {
      const inner = this.innerCanvasRect();
      const hasMessage = this.state.message.trim().length > 0;
      const bandH = hasMessage && this.effectivePlacement() === "band" ? Math.round(inner.h * 0.22) : 0;
      return { x: inner.x, y: inner.y, w: inner.w, h: inner.h - bandH };
    }

    clampPan() {
      if (!this.state.photo || !this.state.photo.bitmap || this.state.photo.remote) return;
      const bmp = this.state.photo.bitmap;
      const area = this.photoAreaCanvasRect();
      this.state.pan = TfkCakeGeometry.clampPan(bmp.width, bmp.height, area.w, area.h, this.state.zoom, this.state.pan);
    }

    /* Crop rect in EXIF-upright source pixels: cover-fit the photo area, then
       zoom. v3.2 moved the rule itself into TfkCakeGeometry so the preview, the
       payload and both repositories' tests all read one implementation. */
    computeCrop() {
      // C40: a stored photo has a crop already. It is drawn from that crop
      // (drawStoredPhoto) and sent as it was; the default pan and zoom a reopened
      // page starts with must never turn into a crop.
      if (!this.state.photo || !this.state.photo.bitmap || this.state.photo.remote) return null;
      const bmp = this.state.photo.bitmap;
      const area = this.photoAreaCanvasRect();
      return TfkCakeGeometry.cropRect(bmp.width, bmp.height, area.w, area.h, this.state.zoom, this.state.pan);
    }

    /* True when this design is zoomed out past the fill point, so the crop must
       be CONTAINED rather than covered. Drives three things that must never
       disagree: the preview's letterbox, the `fit` sent to the server, and the
       low-resolution threshold. */
    containFit() {
      // C40: a stored photo keeps the fit it was saved with, not one read off the slider.
      if (this.state.photo && this.state.photo.remote) return this.state.photo.fit === "contain";
      return TfkCakeGeometry.fitFor(this.state.zoom) === "contain";
    }

    /* v3.2: the slider's left end is the whole-image fit for THIS design and
       THIS upload, so it moves whenever the photo area or the photo does — a
       new upload, an orientation flip, a message appearing or disappearing, a
       placement toggle. Called from drawPreview, which already runs on every
       one of those.

       The percentage is floored so the whole-image state stays reachable
       despite integer rounding (cropDims clamps the overshoot to the image, so
       one step past the exact fit is still exactly the whole image). If the new
       range no longer contains the current zoom, the zoom is clamped up rather
       than left below the floor. The default of 100 is never touched. */
    refreshZoomRange() {
      if (!this.zoomSlider) return;
      const photo = this.state.photo;
      // C40: a stored photo keeps its framing. The slider has nothing to move
      // until the customer uploads a replacement, which is what the note under
      // "Your photo" already says.
      this.zoomSlider.disabled = !!(photo && photo.remote);
      const hasBitmap = !!(photo && photo.bitmap && !photo.remote);
      if (this.zoomHelp) this.zoomHelp.hidden = !hasBitmap;
      if (!hasBitmap) {
        this.zoomSlider.min = "100";
        return;
      }
      const area = this.photoAreaCanvasRect();
      const floor = Math.max(1, Math.floor(TfkCakeGeometry.minZoom(photo.bitmap.width, photo.bitmap.height, area.w, area.h) * 100));
      this.zoomSlider.min = String(floor);
      if (Math.round(this.state.zoom * 100) < floor) {
        this.state.zoom = floor / 100;
        this.zoomSlider.value = String(floor);
        this.clampPan();
      }
    }

    drawPreview() {
      if (!this.canvas || !this.state.config) return;
      // v3.2: the zoom floor depends on the photo area, which this frame is
      // about to recompute. Do it first so the crop below is drawn from a zoom
      // the current range actually allows.
      this.refreshZoomRange();
      const ctx = this.canvas.getContext("2d");
      this.refreshTextSizeRange(ctx); // C44: the fitted size moves with the message, font and orientation
      const cfg = this.state.config;
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.fillStyle = "#efe7dc"; /* canvas fill, not theme CSS */
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      const imgRect = this.mockupCanvasRect();
      if (imgRect) {
        // contract v2.2: contain-fit at natural aspect, never stretch.
        // v3.0: mockupSource() is the 90°-CW-rotated bitmap when portrait.
        ctx.drawImage(this.mockupSource().src, imgRect.x, imgRect.y, imgRect.w, imgRect.h);
      }
      const sheet = this.sheetCanvasRect();
      const hasMessage = this.state.message.trim().length > 0;
      const hasContent = hasMessage || !!this.state.photo;
      const inner = this.innerCanvasRect();
      const area = this.photoAreaCanvasRect();
      if (!imgRect) {
        // Flat-sheet fallback: the white sheet IS the visual here.
        // contract v2.2: translucent empty-state affordance, opaque once there is content
        ctx.fillStyle = hasContent ? "#ffffff" : "rgba(255, 255, 255, 0.75)";
        ctx.fillRect(sheet.x, sheet.y, sheet.w, sheet.h);
      } else if (hasMessage && this.effectivePlacement() === "band") {
        // Hugs-piping request (2026-07-15): with a mockup the physical margin
        // is piped over, so the photo meets the painted piping directly and
        // the sheet is never filled white. The band strip DOES print on white
        // sheet, so white remains under the band region; a message-only band
        // design prints on the bare sheet, so its whole inner rect stays white.
        ctx.fillStyle = "#ffffff";
        if (this.state.photo) {
          const bandTop = area.y + area.h;
          ctx.fillRect(inner.x, bandTop, inner.w, inner.y + inner.h - bandTop);
        } else {
          ctx.fillRect(inner.x, inner.y, inner.w, inner.h);
        }
      }
      const crop = this.computeCrop();
      if (this.state.photo && this.state.photo.remote) {
        // C40: a stored photo is drawn from its stored crop once it has decoded;
        // until then the sheet shows no photo, as before.
        if (this.state.photo.bitmap) this.drawStoredPhoto(ctx, this.state.photo, area);
      } else if (crop && this.state.photo) {
        if (this.containFit()) {
          // v3.2: the whole crop inside the photo area, white around it,
          // mirroring renderArtwork's contain resize onto a white background.
          //
          // The white is painted HERE rather than inherited from the sheet. With
          // a mockup present the sheet is never filled white (hugs-piping,
          // 2026-07-15), so without this fill the border would show the cake
          // photo through it and the preview would promise a cake it can't
          // print. The print pads the same rectangle white inside the resize.
          const box = TfkCakeGeometry.containRect(crop.width, crop.height, area.w, area.h);
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(area.x, area.y, area.w, area.h);
          ctx.drawImage(
            this.state.photo.bitmap, crop.x, crop.y, crop.width, crop.height,
            area.x + box.x * area.w, area.y + box.y * area.h, box.w * area.w, box.h * area.h,
          );
        } else {
          ctx.drawImage(this.state.photo.bitmap, crop.x, crop.y, crop.width, crop.height, area.x, area.y, area.w, area.h);
        }
      }
      if (hasMessage) {
        if (this.v24) {
          this.drawMessage(ctx);
        } else {
          // Pre-v2.4 band renderer, untouched: iterative autoshrink, drawn
          // below the photo (or centred on the sheet with no photo).
          const band = { x: sheet.x, y: area.y + (crop ? area.h : 0), w: sheet.w, h: sheet.h - (crop ? area.h : 0) };
          const textY = crop ? band.y + band.h / 2 : sheet.y + sheet.h / 2;
          const colour = cfg.colours.find((c) => c.key === this.state.colourKey);
          const fontFamily = this.state.fontKey ? `tfk-customiser-${this.state.fontKey}` : "inherit";
          let size = (crop ? band.h : sheet.h) * 0.5;
          ctx.fillStyle = colour ? colour.hex : "#333333";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = `${size}px ${fontFamily}`;
          const maxWidth = sheet.w * 0.9;
          while (size > 12 && ctx.measureText(this.state.message).width > maxWidth) {
            size -= 2;
            ctx.font = `${size}px ${fontFamily}`;
          }
          ctx.fillText(this.state.message, sheet.x + sheet.w / 2, textY);
          this.lastTextBox = null;
        }
      } else {
        this.lastTextBox = null;
        // No message: retract the legibility advisory (drawMessage, which owns
        // its transitions, is not called on this frame).
        if (this.legibilitySlot && this.lowLegibility) {
          this.lowLegibility = false;
          this.legibilitySlot.innerHTML = "";
        }
      }
      this.updateCropWarning();
      this.updateResolutionWarning(crop);
    }

    /* C40: draw a stored photo from its stored crop and fit into the photo area,
       the way the print places it (TfkCakeGeometry.placeStoredCrop). Contain
       paints the area white first for the same reason the live path does: with a
       mockup the sheet is never filled white, and the print pads white. */
    drawStoredPhoto(ctx, photo, area) {
      const fit = photo.fit === "contain" ? "contain" : "cover";
      const placed = TfkCakeGeometry.placeStoredCrop(photo.crop, area.w, area.h, fit);
      if (fit === "contain") {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(area.x, area.y, area.w, area.h);
      }
      ctx.drawImage(
        photo.bitmap, placed.src.x, placed.src.y, placed.src.width, placed.src.height,
        area.x + placed.dest.x * area.w, area.y + placed.dest.y * area.h, placed.dest.w * area.w, placed.dest.h * area.h,
      );
    }

    /* C26 fix 1: say when the customer's framing pushes an edge of the photo
       outside the print area. drawPreview runs per drag frame, so the DOM is
       written only when the set of cut edges changes. */
    updateCropWarning() {
      if (!this.cropWarningSlot) return;
      const bmp = this.state.photo && !this.state.photo.remote && this.state.photo.bitmap;
      let edges = [];
      if (bmp) {
        const area = this.photoAreaCanvasRect();
        edges = TfkCakeGeometry.cutEdges(bmp.width, bmp.height, area.w, area.h, this.state.zoom, this.state.pan);
      }
      const key = edges.join(",");
      if (key === this.cutEdgesKey) return;
      this.cutEdgesKey = key;
      this.cropWarningSlot.innerHTML = "";
      if (edges.length) {
        const warn = document.createElement("p");
        warn.className = "tfk-customiser__warning";
        warn.textContent = TfkCakeGeometry.cutWarningText(edges);
        this.cropWarningSlot.appendChild(warn);
      }
    }

    /* Vertical SIZING box for the message, in preview canvas px (contract
       v2.6). Band strip (22% of inner height) when a photo occupies the sheet,
       else the full inner rect; overlay borrows the band strip so toggling
       placement never resizes text. KEYED ON hasPhoto, never on placement
       alone: a celebration format reuses placement "band" but has no photo, so
       it must size to the full inner rect. A 0.22 strip there would render the
       text ~4.5x too small versus the full-inner print and break parity. */
    messageBoxH() {
      const inner = this.innerCanvasRect();
      const hasPhoto = !!this.state.photo;
      if (this.effectivePlacement() === "overlay") return Math.round(inner.h * 0.22);
      return hasPhoto ? Math.round(inner.h * 0.22) : inner.h;
    }

    /* The customer's message reduced to canonical lines, mirroring the server's
       canonicaliseMessage (message.ts): CRLF and lone CR both become LF, each
       line trimmed, empty lines dropped. Baking, preview, POST body, and print
       therefore all agree on the same lines. */
    canonicalTypedLines() {
      return this.state.message
        .replace(/\r\n?/g, "\n")
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    }

    /* Lines baked for BOTH preview and POST /designs (contract v2.6). The theme
       owns every line break: the customer's Enter presses (canonical lines) plus
       any auto-wrap, all sent as literal \n; the server renders them verbatim
       and never re-wraps, so preview and print agree by construction. Auto-wrap
       runs ONLY where the sizing box is the full inner rect, i.e. a message-only
       band render (a celebration format, or a photo-capable format with the
       white strip ticked and no photo). There stacking lines fills a tall box
       and keeps the font large. The photo band strip and the overlay box are
       both the short 0.22 bandBox, where extra lines only shrink the font, so
       those paths respect the customer's own Enter breaks only (owner decision
       2026-07-16, refined per geometry review). */
    bakedLines(ctx) {
      const typed = this.canonicalTypedLines();
      if (!this.v26 || typed.length === 0) return typed;
      if (this.state.photo || this.effectivePlacement() !== "band") return typed;
      // C44: a Celebration message the customer shrank is rewrapped at the
      // chosen size, which puts more words on each line. At the default (no
      // size) this is exactly the fitted wrap it has always been.
      const capMm = this.textSizeCapMm(ctx);
      if (capMm != null) return this.wrapAtSize(ctx, typed, capMm * this.canvasPxPerMm());
      return this.wrapToBox(ctx, typed);
    }

    bakedMessage() {
      const ctx = this.canvas && this.canvas.getContext("2d");
      return this.bakedLines(ctx).join("\n");
    }

    /* Greedily split the widest line at its most balanced space WHILE the block
       is width-bound (widthFit < 0.6 x lineBoxH). Each split narrows the widest
       line (widthFit rises) and adds a line (0.6 x lineBoxH falls); stopping at
       the crossover lands at the n that maximises the uniform fontSize, i.e.
       fills the box. Terminates: n only grows, bounded by word count (a single
       unsplittable word rides the floor and clips, matching the server) and by
       messageMaxChars. */
    wrapToBox(ctx, typed) {
      if (!ctx) return typed;
      const inner = this.innerCanvasRect();
      const totalBoxH = this.messageBoxH();
      const fontFamily = this.state.fontKey ? `tfk-customiser-${this.state.fontKey}` : "inherit";
      let lines = typed.slice();
      for (let guard = 0; guard < this.state.config.format.messageMaxChars; guard++) {
        const n = lines.length;
        ctx.font = `100px ${fontFamily}`;
        const adv100 = lines.map((line) => this.lineAdvance(ctx, line, 100));
        let widthFit = Infinity;
        for (const a of adv100) if (a > 0) widthFit = Math.min(widthFit, (inner.w * 0.9 * 100) / a);
        const heightBound = 0.6 * (totalBoxH / n);
        if (widthFit >= heightBound) break; // height-bound: font already maxed for the box
        let wi = 0;
        for (let i = 1; i < n; i++) if (adv100[i] > adv100[wi]) wi = i;
        const split = this.splitBalanced(lines[wi]);
        if (!split) break; // widest line is a single word: cannot improve
        lines = lines.slice(0, wi).concat(split, lines.slice(wi + 1));
      }
      return lines;
    }

    /* Split one line into two word-balanced halves at the space that minimises
       the character-length difference. Null when the line has no breakable
       space (a single word). */
    splitBalanced(line) {
      const words = line.split(/\s+/).filter(Boolean);
      if (words.length < 2) return null;
      let best = 1;
      let bestDiff = Infinity;
      for (let i = 1; i < words.length; i++) {
        const left = words.slice(0, i).join(" ").length;
        const right = words.slice(i).join(" ").length;
        const diff = Math.abs(left - right);
        if (diff < bestDiff) { bestDiff = diff; best = i; }
      }
      return [words.slice(0, best).join(" "), words.slice(best).join(" ")];
    }

    /* ---- Contract v3.3 (C44): the Celebration text size ----

       The slider's right end is today's message: as large as fits the printable
       area. Dragging left shrinks it, and the message is rewrapped at the
       smaller size, so more words fit on each line. It never enlarges past the
       fitted size, and never goes below MIN_TEXT_SIZE_MM (ruled 29 September
       2026, the size under which updateLegibility already warns).

       The chosen size is kept, sent and stored in MILLIMETRES
       (state.messageSizeMm, null = as large as fits). The print applies it as a
       cap on its own fitted size for the same lines, which is the same rule
       messageFontSize applies here, so the preview and the print file agree the
       way they already did about the fitted size. */

    /* True when this design offers the Text size slider. */
    textSizeEnabled() {
      return !!(this.v33 && this.v26 && this.isCelebration());
    }

    canvasPxPerMm() {
      return this.sheetCanvasRect().w / this.sheetWMm();
    }

    /* The shared text rule (contract v2.4/v2.6) for these lines, in preview
       canvas px, capped at capPx when the customer chose a smaller size. With
       no cap this is, operation for operation, the size drawMessage has always
       drawn; the cap is applied before the floor, exactly as the print does. */
    messageFontSize(ctx, lines, capPx) {
      const sheet = this.sheetCanvasRect();
      const inner = this.innerCanvasRect();
      const fontFamily = this.state.fontKey ? `tfk-customiser-${this.state.fontKey}` : "inherit";
      const lineBoxH = this.messageBoxH() / lines.length;
      ctx.font = `100px ${fontFamily}`;
      let widthFit = Infinity;
      for (const line of lines) {
        const a = this.lineAdvance(ctx, line, 100);
        if (a > 0) widthFit = Math.min(widthFit, (inner.w * 0.9 * 100) / a);
      }
      if (!isFinite(widthFit)) widthFit = lineBoxH; // all-blank guard (not normally reached)
      const floorPx = (8 * (sheet.w / this.sheetWMm())) / (this.state.config.format.dpi / 25.4);
      const fitted = Math.min(lineBoxH * 0.6, widthFit);
      const capped = capPx != null ? Math.min(fitted, capPx) : fitted;
      return Math.max(floorPx, capped);
    }

    /* Today's size for this message, font and orientation: the fitted wrap at
       its fitted size. The slider's right end. */
    fittedSizeMm(ctx) {
      const typed = this.canonicalTypedLines();
      if (!typed.length) return 0;
      return this.messageFontSize(ctx, this.wrapToBox(ctx, typed), null) / this.canvasPxPerMm();
    }

    /* The chosen size when it actually shrinks the message, else null. A stored
       size at or above what fits prints at what fits, so it is no size at all. */
    textSizeCapMm(ctx) {
      if (!this.textSizeEnabled() || !ctx) return null;
      const size = this.state.messageSizeMm;
      if (typeof size !== "number" || !(size > 0)) return null;
      return size < this.fittedSizeMm(ctx) ? size : null;
    }

    /* Set the size from a share of what fits (the slider's value / 100).
       Rounded to 0.01 mm HERE, so the preview draws exactly the number that is
       sent, and never below the minimum. A share of 1 or more is no size. */
    setTextSizeShare(ctx, share) {
      const fitted = this.fittedSizeMm(ctx);
      if (!(share < 1) || !(fitted > MIN_TEXT_SIZE_MM)) {
        this.state.messageSizeMm = null;
        return;
      }
      const size = Math.max(MIN_TEXT_SIZE_MM, Math.round(fitted * share * 100) / 100);
      this.state.messageSizeMm = size < fitted ? size : null;
    }

    /* Each typed line broken into the FEWEST lines that fit 90% of the inner
       width at sizePx, and among those, the break that makes the widest line
       as short as possible, so the block stays balanced like the fitted wrap.
       The customer's own line breaks are kept. A word too long to fit alone
       gets a line of its own and clips, as it does at the fitted size. */
    wrapAtSize(ctx, typed, sizePx) {
      const inner = this.innerCanvasRect();
      const fontFamily = this.state.fontKey ? `tfk-customiser-${this.state.fontKey}` : "inherit";
      ctx.font = `100px ${fontFamily}`;
      const limit100 = (inner.w * 0.9 * 100) / sizePx; // widest advance allowed, measured at 100px
      const out = [];
      for (const line of typed) {
        const words = line.split(/\s+/).filter(Boolean);
        const n = words.length;
        if (n < 2) { out.push(line); continue; }
        // w[i][j]: the advance at 100px of words i..j joined by single spaces.
        const w = [];
        for (let i = 0; i < n; i++) {
          w[i] = [];
          for (let j = i; j < n; j++) w[i][j] = this.lineAdvance(ctx, words.slice(i, j + 1).join(" "), 100);
        }
        // best[j]: the smallest possible widest line over words 0..j-1 in k
        // lines, with cut[k][j] recording where its last line starts.
        let best = [0].concat(words.map((_, j) => w[0][j]));
        const cut = [null, new Array(n + 1).fill(0)];
        let lines = 1;
        while (best[n] > limit100 && lines < n) {
          lines += 1;
          const next = new Array(n + 1).fill(Infinity);
          cut[lines] = new Array(n + 1).fill(0);
          for (let j = lines; j <= n; j++) {
            for (let s = lines - 1; s < j; s++) {
              const worst = Math.max(best[s], w[s][j - 1]);
              if (worst < next[j]) { next[j] = worst; cut[lines][j] = s; }
            }
          }
          best = next;
        }
        const parts = [];
        let end = n;
        for (let k = lines; k >= 1; k--) {
          const start = k === 1 ? 0 : cut[k][end];
          parts.unshift(words.slice(start, end).join(" "));
          end = start;
        }
        out.push(...parts);
      }
      return out;
    }

    buildTextSizeControl() {
      this.textSizeSlider = document.createElement("input");
      this.textSizeSlider.type = "range";
      this.textSizeSlider.className = "tfk-customiser__zoom";
      this.textSizeSlider.min = "10";
      this.textSizeSlider.max = "100";
      this.textSizeSlider.value = "100";
      this.textSizeSlider.setAttribute("aria-label", "Text size");
      this.textSizeSlider.addEventListener("input", () => {
        const ctx = this.canvas && this.canvas.getContext("2d");
        if (!ctx) return;
        this.setTextSizeShare(ctx, Number(this.textSizeSlider.value) / 100);
        this.drawPreview();
      });
      const help = document.createElement("p");
      help.className = "tfk-customiser__note tfk-customiser__zoom-help";
      help.textContent = "Drag left to make the text smaller and fit more words on each line.";
      const wrap = document.createElement("div");
      wrap.className = "tfk-customiser__zoom-control";
      wrap.append(this.textSizeSlider, help);
      return wrap;
    }

    /* Keeps the slider in step with the message, font and orientation, which
       all move the fitted size. Called from drawPreview, which runs on every
       one of those. The left end is the 10 mm minimum as a share of what fits;
       when what fits is already 10 mm or less there is nothing to shrink, and
       the slider rests, disabled, at the right. */
    refreshTextSizeRange(ctx) {
      const slider = this.textSizeSlider;
      if (!slider || !ctx) return;
      const fitted = this.canonicalTypedLines().length ? this.fittedSizeMm(ctx) : 0;
      const canShrink = fitted > MIN_TEXT_SIZE_MM;
      slider.disabled = !canShrink;
      const min = canShrink ? Math.min(100, Math.ceil((MIN_TEXT_SIZE_MM / fitted) * 100)) : 100;
      if (slider.min !== String(min)) slider.min = String(min);
      const cap = this.textSizeCapMm(ctx);
      const value = cap == null ? 100 : Math.max(min, Math.min(100, Math.round((cap / fitted) * 100)));
      if (slider.value !== String(value)) slider.value = String(value);
    }

    /* The text size a save sends: the chosen size while it shrinks the
       message, else null. Only on a design that offers the slider. */
    messageSizeForSave() {
      const ctx = this.canvas && this.canvas.getContext("2d");
      return this.textSizeCapMm(ctx);
    }

    /* Non-blocking legibility advisory (contract v2.6): warn when the uniform
       font size is under 10 mm at real-world scale. fontSize_canvas / pxPerMm is
       algebraically the contract's fontSize_px / (dpi/25.4) and scale-independent.
       Mirrors updateResolutionWarning: DOM writes only on state transitions. */
    updateLegibility(fontSize, sheet) {
      if (!this.legibilitySlot) return;
      const pxPerMm = sheet.w / this.sheetWMm();
      const mm = pxPerMm > 0 ? fontSize / pxPerMm : Infinity;
      const low = mm < 10;
      if (low === this.lowLegibility) return;
      this.lowLegibility = low;
      this.legibilitySlot.innerHTML = "";
      if (low) {
        const warn = document.createElement("p");
        warn.className = "tfk-customiser__warning";
        warn.textContent = "This message may look small when printed. Fewer words, or shorter lines, will print larger and clearer.";
        this.legibilitySlot.appendChild(warn);
      }
    }

    /* Contract v2.4/v2.4.1 + v2.6: shared deterministic text rule, identical in
       preview and print, generalised to n lines. n = 1 reproduces every v2.4
       number exactly (verified against render.server.ts). Draws in band or
       overlay placement; overlay carries an unconditional legibility shadow and
       is draggable (see bindPan). */
    drawMessage(ctx) {
      const cfg = this.state.config;
      const sheet = this.sheetCanvasRect();
      const inner = this.innerCanvasRect();
      const hasPhoto = !!this.state.photo;
      const placement = this.effectivePlacement();
      const colour = cfg.colours.find((c) => c.key === this.state.colourKey);
      const fontFamily = this.state.fontKey ? `tfk-customiser-${this.state.fontKey}` : "inherit";
      const fillColour = colour ? colour.hex : "#333333";

      // Baked lines drive sizing AND the POST body, so preview == print. When
      // the backend is < 2.6 this is a single canonical line, so the whole path
      // collapses to the exact v2.4 single-line render below.
      const lines = this.v26 ? this.bakedLines(ctx) : [this.state.message.trim()];
      const n = lines.length;
      if (n === 0) { this.lastTextBox = null; return; }

      // Uniform font size: width-fit is governed by the widest line (min over
      // lines of the size at which a line's advance hits 90% of inner width),
      // clamped against the per-line height budget and the print-DPI floor. No
      // iteration, mirrors the server renderer exactly. C44: capped at the
      // Celebration text size when the customer shrank it (messageFontSize).
      const capMm = this.v26 ? this.textSizeCapMm(ctx) : null;
      const fontSize = this.messageFontSize(ctx, lines, capMm != null ? capMm * this.canvasPxPerMm() : null);
      ctx.font = `${fontSize}px ${fontFamily}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const LINE_ADVANCE = 1.2;

      this.updateLegibility(fontSize, sheet);

      const adv = lines.map((line) => this.lineAdvance(ctx, line, fontSize));
      const maxAdv = adv.reduce((m, a) => Math.max(m, a), 0);

      // Draw every line at the uniform size, each independently horizontally
      // centred on cx, stacked LINE_ADVANCE em apart around the block centre cy.
      // Contract v3.5: a line with emoji is drawn piece by piece
      // (drawWritingLine); a line without is drawn exactly as before. v3.6
      // adds symbols, and a refused character is left out of the drawing.
      const drawLines = (cx, cy, withShadow) => {
        for (let i = 0; i < n; i++) {
          const y = cy + (i - (n - 1) / 2) * LINE_ADVANCE * fontSize;
          if (withShadow) {
            ctx.fillStyle = "rgba(0,0,0,0.35)";
            const sx = cx + 0.02 * fontSize;
            const sy = y + 0.02 * fontSize;
            if (!this.drawWritingLine(ctx, lines[i], sx, sy, fontSize, true)) ctx.fillText(lines[i], sx, sy);
          }
          ctx.fillStyle = fillColour;
          if (!this.drawWritingLine(ctx, lines[i], cx, y, fontSize, false)) ctx.fillText(lines[i], cx, y);
        }
      };

      if (placement === "band") {
        const bandH = hasPhoto ? Math.round(inner.h * 0.22) : 0;
        const cx = inner.x + inner.w / 2;
        const cy = hasPhoto ? inner.y + (inner.h - bandH) + bandH / 2 : inner.y + inner.h / 2;
        // Clip boundary (contract v2.6): band+photo clips at the band strip;
        // message-only / celebration clips at the full inner rect. Only applied
        // for multi-line, so a v2.4 single-line band render stays byte-identical
        // (v2.4 band did not clip; a fitting single line clips to nothing anyway).
        const clip = n > 1;
        if (clip) {
          ctx.save();
          ctx.beginPath();
          if (hasPhoto) ctx.rect(inner.x, inner.y + inner.h - bandH, inner.w, bandH);
          else ctx.rect(inner.x, inner.y, inner.w, inner.h);
          ctx.clip();
        }
        drawLines(cx, cy, false);
        if (clip) ctx.restore();
        this.lastTextBox = null;
        return;
      }

      // Overlay: anchor is in SHEET fractions; the clamp block (max advance over
      // all lines x n x LINE_ADVANCE x fontSize) is shifted minimally to fit
      // inside inner (the margin-inset printable rect); ink clips at inner.
      const anchor = this.state.messageAnchor;
      const rawCx = sheet.x + anchor.x * sheet.w;
      const rawCy = sheet.y + anchor.y * sheet.h;
      const boxW = maxAdv;
      const clampH = n * LINE_ADVANCE * fontSize;
      const x = boxW >= inner.w
        ? inner.x + (inner.w - boxW) / 2
        : Math.min(Math.max(rawCx - boxW / 2, inner.x), inner.x + inner.w - boxW);
      const y = clampH >= inner.h
        ? inner.y + (inner.h - clampH) / 2
        : Math.min(Math.max(rawCy - clampH / 2, inner.y), inner.y + inner.h - clampH);
      const cx = x + boxW / 2;
      const cy = y + clampH / 2;

      ctx.save();
      ctx.beginPath();
      ctx.rect(inner.x, inner.y, inner.w, inner.h);
      ctx.clip();
      drawLines(cx, cy, true);
      ctx.restore();
      this.lastTextBox = { x, y, w: boxW, h: clampH };
    }

    /* Low-resolution acknowledgement row: lives at the bottom with the other
       acknowledgement checkboxes; shown only while the crop is below print
       density (toggled by updateResolutionWarning). */
    buildLowResRow() {
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      row.hidden = true;
      const label = document.createElement("label");
      label.className = "tfk-customiser__note";
      const box = document.createElement("input");
      box.type = "checkbox";
      box.id = "tfk-customiser-lowres";
      box.addEventListener("change", () => {
        this.state.lowResAccepted = box.checked;
        this.refreshValidity();
      });
      this.lowResBox = box;
      label.append(box, document.createTextNode(" I understand this photo is below the recommended resolution and the print may look blurry"));
      row.appendChild(label);
      return row;
    }

    /* Warning stays under the preview; the acknowledgement row at the bottom
       shows and hides with it. Updates only on state transitions (drawPreview
       runs per drag frame). */
    /* How much of the photo area's width the printed image actually spans.

       1 under cover, which is the assumption minPrintWidthPx is built on. Under
       contain a letterboxed image prints NARROWER than the area, so the same
       source pixels land on less paper and the threshold scales down with it.

       This corrects a test that is otherwise too strict on the letterboxed axis
       and would warn about a photo that prints sharper than one it passes. It
       does not make the warning more accurate in absolute terms: every
       production format still carries the placeholder minEffectiveDpi of 72,
       which nobody has measured, so the warning is barely firing either way. */
    printedWidthFraction(crop) {
      if (!this.containFit()) return 1;
      const area = this.photoAreaCanvasRect();
      return TfkCakeGeometry.containRect(crop.width, crop.height, area.w, area.h).w;
    }

    updateResolutionWarning(crop) {
      const low = !!(crop && crop.width < this.minPrintWidthPx() * this.printedWidthFraction(crop));
      if (low === this.lowResActive) return;
      this.lowResActive = low;
      this.resolutionSlot.innerHTML = "";
      if (low) {
        const warn = document.createElement("p");
        warn.className = "tfk-customiser__warning";
        warn.textContent = "This photo may look blurry when printed at this zoom. A larger photo or less zoom will print sharper.";
        this.resolutionSlot.appendChild(warn);
      }
      if (this.lowResRow) {
        this.lowResRow.hidden = !low;
        if (low && this.lowResBox) this.lowResBox.checked = this.state.lowResAccepted;
      }
      this.refreshValidity();
    }

    buildRightsRow() {
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      const label = document.createElement("label");
      label.className = "tfk-customiser__note";
      const box = document.createElement("input");
      box.type = "checkbox";
      box.addEventListener("change", () => {
        this.state.rightsAccepted = box.checked;
        this.refreshValidity();
      });
      label.append(box, document.createTextNode(" I have the right to use this image"));
      row.appendChild(label);
      return row;
    }

    /* Print acknowledgement: applies to every design, photo or not. Client-side
       gate only, not part of the design spec sent to the server. */
    buildPrintNoticeRow() {
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      const label = document.createElement("label");
      label.className = "tfk-customiser__note";
      const box = document.createElement("input");
      box.type = "checkbox";
      box.id = "tfk-customiser-print-notice";
      box.addEventListener("change", () => {
        this.state.printAccepted = box.checked;
        this.refreshValidity();
      });
      // Celebration cakes carry no photo, so the "edible image paper" / "regular
      // photo" comparison does not apply; keep the colour-subduing and handmade
      // caveats in text-only terms. Photo formats keep the original wording.
      const notice = this.isCelebration()
        ? " I understand that colours printed on the icing sheet are more subdued than they appear on screen, and that the finished cake may differ from the preview as each one is made by hand"
        : " I understand that colours printed on edible image paper are more subdued than a regular photo, and that the finished cake may differ from the preview as each one is made by hand";
      label.append(box, document.createTextNode(notice));
      row.appendChild(label);
      return row;
    }

    /* Contract v2.8: one submit action. The label must not promise
       confirmation, because payment status is unknowable pre-submit (no
       reliable signal in the GET payload; orderRef and pickup.date are
       explicitly not heuristics). The PATCH response's state field decides
       what happens next. */
    buildSaveRow() {
      const row = document.createElement("div");
      row.className = "tfk-customiser__row";
      this.saveBtn = document.createElement("button");
      this.saveBtn.type = "button";
      this.saveBtn.className = "tfk-customiser__save";
      this.saveBtn.textContent = "Save my design";
      this.saveBtn.addEventListener("click", () => this.submitComplete());
      this.statusSlot = document.createElement("div");
      row.append(this.saveBtn, this.statusSlot);
      return row;
    }

    /* ---- Contract v2.9: unpaid-checkout states ---- */

    /* "Complete your purchase" link, or null when there is nothing safe to
       link: a null invoiceUrl (unpaid legacy design, or an invoice link that
       was never captured) must degrade to phone-call copy, never a button
       with a broken href. Opens in a new tab so an unsaved design isn't
       navigated away from; the token stays live either way. */
    buildPayCta() {
      const url = this.payment && typeof this.payment.invoiceUrl === "string" && this.payment.invoiceUrl
        ? this.payment.invoiceUrl
        : null;
      if (!url) return null;
      const link = document.createElement("a");
      link.className = "tfk-customiser__pay-link";
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener";
      link.textContent = "Complete your purchase";
      return link;
    }

    buildPaymentNotice(copyWithCta, copyWithoutCta) {
      const wrap = document.createElement("div");
      wrap.className = "tfk-customiser__payment";
      const copy = document.createElement("p");
      copy.className = "tfk-customiser__payment-copy";
      const cta = this.buildPayCta();
      copy.textContent = cta ? copyWithCta : copyWithoutCta;
      wrap.appendChild(copy);
      if (cta) wrap.appendChild(cta);
      return wrap;
    }

    /* Single-CTA rule: the unpaid banner (above the form) and the post-save
       note (next to the save button) are mutually exclusive — exactly one
       payment call-to-action on screen. Each renderer clears the other's
       slot. payment is captured once at prefill and never re-fetched: a
       PATCH can't change paid/invoiceUrl, and a design paid mid-session
       comes back from PATCH as state "confirmed", which replaces the form
       before a stale unpaid state could show. */
    renderUnpaidBanner() {
      if (!this.paymentSlot) return;
      this.paymentSlot.innerHTML = "";
      if (!this.payment || this.payment.paid !== false) return;
      this.paymentSlot.appendChild(
        this.buildPaymentNotice("Not paid yet", "Not paid yet. Please call us to pay."),
      );
    }

    renderPostSaveUnpaid() {
      if (this.paymentSlot) this.paymentSlot.innerHTML = "";
      if (!this.statusSlot) return;
      this.statusSlot.appendChild(
        this.buildPaymentNotice(
          "Design saved. We'll start on your order once payment is received.",
          "Design saved. We'll start on your order once payment is received.",
        ),
      );
    }

    /* True when the customer touched photos: any locally uploaded photo, a
       changed slot count, or a removed stored photo. Remote-only and
       untouched means the field is OMITTED from the PATCH (partial update
       preserves it server-side). Local photos always count as changed, so a
       second save re-sends the same array; that is idempotent and cheap. */
    photosChanged() {
      const snap = Array.isArray(this.snapshot.photos) ? this.snapshot.photos : [];
      if (this.isCupcakeAllocator()) {
        const imgs = this.state.images;
        if (imgs.some((im) => !im.remote)) return true;
        if (imgs.length !== snap.length) return true;
        return imgs.some((im, i) => {
          const snapCount = Number.isInteger(snap[i] && snap[i].count) && snap[i].count >= 1 ? snap[i].count : 1;
          return im.count !== snapCount;
        });
      }
      return !!(this.state.photo && !this.state.photo.remote && this.state.photo.uploadState === "done");
    }

    /* Contract v2.8 PATCH body: token plus ONLY the fields the customer
       changed. Explicit null clears. NEVER formatKey/quantity/packSize (the
       locked triplet); the server 422s on their mere presence. */
    buildPatchBody() {
      const body = { token: this.token };
      const cfg = this.state.config;
      const snap = this.snapshot;
      // Contract v3.0: orientation joins the partial merge. Sent only when
      // it actually changed: "portrait" to set, explicit null to clear back
      // to landscape (the documented clear path). Gated on v30, which in
      // complete mode already means the GET reported rotatable: true.
      if (this.v30) {
        const snapOrientation = snap.orientation === "portrait" ? "portrait" : "landscape";
        if (this.state.orientation !== snapOrientation) {
          body.orientation = this.isPortrait() ? "portrait" : null;
        }
      }
      if (!this.isCupcakeAllocator() && cfg.format.supportsMessage) {
        const baked = this.state.message.trim().length
          ? (this.v26 ? this.bakedMessage() : this.state.message.trim())
          : "";
        const layout = baked && this.v24 && this.effectivePlacement() === "overlay"
          ? {
              placement: "overlay",
              anchor: {
                x: Math.min(1, Math.max(0, this.state.messageAnchor.x)),
                y: Math.min(1, Math.max(0, this.state.messageAnchor.y)),
              },
            }
          : null;
        const snapMsg = typeof snap.message === "string" ? snap.message : "";
        const snapLayout = snap.messageLayout && snap.messageLayout.placement === "overlay" ? snap.messageLayout : null;
        const layoutKey = (l) => (l && l.anchor ? l.anchor.x.toFixed(3) + "," + l.anchor.y.toFixed(3) : "none");
        // C44: the text size travels with the message it sizes. Compared and
        // sent only where the slider is offered, so any other design's PATCH
        // is byte-identical to what it was before v3.3.
        const sized = this.textSizeEnabled();
        const size = sized && baked ? this.messageSizeForSave() : null;
        const snapSize = typeof snap.messageSizeMm === "number" ? snap.messageSizeMm : null;
        const changed = baked !== snapMsg
          || (!!baked && (this.state.fontKey !== snap.fontKey || this.state.colourKey !== snap.colourKey))
          || layoutKey(layout) !== layoutKey(snapLayout)
          || (sized && size !== snapSize);
        if (changed) {
          body.message = baked || null;
          if (baked) {
            body.fontKey = this.state.fontKey;
            body.colourKey = this.state.colourKey;
          }
          body.messageLayout = layout;
          if (sized) body.messageSizeMm = size;
        }
      }
      if (this.photosChanged()) {
        body.photos = this.isCupcakeAllocator()
          ? this.state.images.map((im) => ({ key: im.key, crop: this.computeCropForSlot(im), count: im.count }))
          : [this.slabPhotoPayload()];
      }
      return body;
    }

    /* The slab photo entry for POST and PATCH (contract v3.2).

       A LOCAL photo carries the crop and fit this session computed. A REMOTE
       one (a stored photo on the completion page, no bitmap to recompute from)
       passes its stored crop AND its stored fit straight through: dropping the
       fit here would silently un-contain a design the customer already
       approved, and the border would vanish between the screen and the sheet.

       `fit` is omitted whenever it would be "cover", so a design nobody
       re-zooms posts exactly the body it posted before v3.2. */
    slabPhotoPayload() {
      const photo = this.state.photo;
      // C40: keyed on `remote`, not on the bitmap. A stored photo now has a bitmap
      // (hydrateRemotePhotos), and recomputing its crop from the default pan and
      // zoom would reframe a design the customer had already approved.
      if (photo.remote || !photo.bitmap) {
        return {
          key: photo.key,
          crop: photo.crop,
          ...(photo.fit === "contain" ? { fit: "contain" } : {}),
        };
      }
      return {
        key: photo.key,
        crop: this.computeCrop(),
        ...(this.containFit() ? { fit: "contain" } : {}),
      };
    }

    /* After a state:"saved" response, fold the sent fields into the snapshot
       so the next diff is relative to what the server now holds. The photos
       line matters: without it, removing a photo that a previous save added
       would make the UI match the stale snapshot again, photosChanged()
       would return false, and the removal would be silently omitted from the
       next PATCH (the server would keep the photo). The sent photos array is
       already {key, crop, count} shaped and index-aligned with state.images.
       Local slots still re-send on every save via the some(!remote) check in
       photosChanged; that stays idempotent. */
    applySavedSnapshot(body) {
      // v3.0: null cleared the stored value back to landscape server-side.
      if ("orientation" in body) this.snapshot.orientation = body.orientation === "portrait" ? "portrait" : "landscape";
      if ("message" in body) this.snapshot.message = body.message;
      if ("fontKey" in body) this.snapshot.fontKey = body.fontKey;
      if ("colourKey" in body) this.snapshot.colourKey = body.colourKey;
      if ("messageLayout" in body) this.snapshot.messageLayout = body.messageLayout;
      if ("messageSizeMm" in body) this.snapshot.messageSizeMm = body.messageSizeMm;
      if ("photos" in body) this.snapshot.photos = body.photos;
    }

    /* Contract v2.8: the PATCH response's state field is authoritative.
       saved = unpaid design, keep editing. confirmed = paid design, terminal
       (the token was consumed in the same write; any later request 404/409s).
       404 and 409 are ONE outcome: the expired panel, no retry affordance. */
    async submitComplete() {
      const v = this.validate();
      this.errorSlot.innerHTML = "";
      if (this.statusSlot) this.statusSlot.innerHTML = "";
      // A new submit leaves the post-save state: the banner comes back until
      // the PATCH lands (single-CTA rule, contract v2.9). No-op when paid or
      // the gate is down.
      this.renderUnpaidBanner();
      if (!v.ok) {
        this.errorSlot.appendChild(this.buildError(v.reason));
        return;
      }
      const body = this.withContractFields(this.buildPatchBody());
      this.state.saving = true;
      this.refreshValidity();
      try {
        const res = await fetch(`${API_BASE}/design`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (res.status === 409) {
          // Contract v3.4: this cake already has its design, and the save did
          // not come from "Change this cake's design". Nothing was replaced;
          // the cake's own page says so and leads to the cake still waiting.
          let refused = null;
          try { refused = await res.json(); } catch { refused = null; }
          if (this.alreadyDesigned(refused)) { this.goToCakePage(false); return; }
          this.renderExpired();
          return;
        }
        if (res.status === 404) { this.renderExpired(); return; }
        if (res.status === 429) {
          this.errorSlot.appendChild(this.buildError("Please wait a moment and try again."));
          return;
        }
        if (res.status === 422) {
          const data = await res.json();
          this.errorSlot.appendChild(this.buildError(data.error || "That could not be saved. Please try again."));
          return;
        }
        if (!res.ok) throw new Error(`design ${res.status}`);
        const data = await res.json();
        if (data.state === "confirmed") {
          // Contract v3.4: another cake on the order still waits, so "We have
          // everything we need" would be true of this cake only. The cake's
          // own page says this one is done and takes the customer to the next.
          if (this.anotherCakeWaits(data)) { this.goToCakePage(true); return; }
          this.renderConfirmed(true);
          return;
        }
        // state is authoritative: only "saved" is a success here. Any other
        // 200 shape is unknown and takes the generic failure path.
        if (data.state !== "saved") throw new Error(`design state ${data.state}`);
        this.applySavedSnapshot(body);
        // Contract v2.9: for a known-unpaid design the richer post-save state
        // replaces the plain confirmation (and the unpaid banner with it).
        // Gate down or paid (which shouldn't reach "saved"): plain v2.8 note.
        if (this.payment && this.payment.paid === false) {
          this.renderPostSaveUnpaid();
        } else {
          const note = document.createElement("p");
          note.className = "tfk-customiser__banner";
          note.textContent = "Your design has been saved. You can come back to this link to make changes.";
          this.statusSlot.appendChild(note);
        }
      } catch (err) {
        console.warn("[customiser] complete save failed", err);
        this.errorSlot.appendChild(this.buildError("Could not save your design. Please try again."));
      } finally {
        this.state.saving = false;
        if (this.saveBtn) this.refreshValidity();
      }
    }

    /* Bound on document, not this.form. Capture-phase order for two listeners on
       the SAME target element depends on registration order, which is fragile;
       document-level capture genuinely precedes any target-phase listener on
       the form regardless of who registered first. That guarantee is what lets
       stopImmediatePropagation() suppress the cart drawer's submit handler on
       the first pass, while the second pass (designReady consumed) still falls
       through to the drawer's form-level listener untouched. */
    bindSubmit() {
      if (!this.form) return;
      document.addEventListener(
        "submit",
        (e) => {
          if (e.target !== this.form) return;
          if (this.designReady) { this.designReady = false; return; } // second pass: let the drawer interceptor run
          e.preventDefault();
          e.stopImmediatePropagation();
          this.saveDesignAndResubmit();
        },
        { capture: true },
      );
    }

    async saveDesignAndResubmit() {
      const v = this.validate();
      this.errorSlot.innerHTML = "";
      if (!v.ok) { this.errorSlot.appendChild(this.buildError(v.reason)); return; }
      this.state.saving = true;
      this.refreshValidity();
      try {
        const cfg = this.state.config;
        const cupcake = this.isCupcakeAllocator();
        const hasMessage = !cupcake && this.state.message.trim().length > 0;
        let body;
        if (cupcake) {
          // contract v2.7: per-image disc counts plus body-level quantity (live
          // from the native selector) and packSize; no message fields. The baked
          // crop per slot may be non-square; the server contains it on a white disc.
          body = {
            formatKey: this.formatKey,
            rightsAccepted: this.state.rightsAccepted,
            photos: this.state.images.map((im) => ({ key: im.key, crop: this.computeCropForSlot(im), count: im.count })),
            quantity: this.nativeQty(),
            packSize: this.packSize,
          };
        } else {
          body = {
            formatKey: this.formatKey,
            rightsAccepted: this.state.rightsAccepted,
            photos: this.state.photo && this.state.photo.uploadState === "done"
              ? [this.slabPhotoPayload()]
              : [],
          };
          if (hasMessage) {
            // contract v2.6: send the baked message with every line break (typed
            // and auto-wrapped) as a literal \n; the server renders it verbatim.
            // Pre-2.6 sends the single trimmed line exactly as before.
            body.message = this.v26 ? this.bakedMessage() : this.state.message.trim();
            body.fontKey = this.state.fontKey;
            body.colourKey = this.state.colourKey;
            // C44: only when the customer shrank a Celebration message; omitted
            // means as large as fits, so an untouched design posts what it did.
            const size = this.textSizeEnabled() ? this.messageSizeForSave() : null;
            if (size != null) body.messageSizeMm = size;
          }
          // Contract v2.4: send messageLayout only when the config reports
          // contract >= 2.4 and the customer kept the overlay placement; band
          // (or a stale backend) omits the field entirely.
          if (hasMessage && this.v24 && this.effectivePlacement() === "overlay") {
            body.messageLayout = {
              placement: "overlay",
              anchor: {
                x: Math.min(1, Math.max(0, this.state.messageAnchor.x)),
                y: Math.min(1, Math.max(0, this.state.messageAnchor.y)),
              },
            };
          }
        }
        // Contract v3.0: omitted = landscape, so only a portrait choice is
        // sent, and only when the gate (contract >= 3.0 AND rotatable)
        // offered the toggle in the first place.
        if (this.v30 && this.isPortrait()) body.orientation = "portrait";
        const res = await fetch(`${API_BASE}/designs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (res.status === 422) {
          const { error } = await res.json();
          throw Object.assign(new Error(error), { display: true });
        }
        if (res.status !== 201) throw new Error(`designs ${res.status}`);
        const { designId } = await res.json();
        // contract v2.7: the design's allocation is pinned to the quantity read
        // at save time. If the native selector changed during the in-flight
        // save, the cart line would carry the new quantity while the design
        // reflects the old pool. Abort rather than ship a mismatch; the unsaved
        // design row is cheap (expires in 14 days) and the customer re-adds.
        if (cupcake && this.nativeQty() !== body.quantity) {
          throw Object.assign(
            new Error("Your quantity changed while saving. Please review your cupcake allocation and add to cart again."),
            { display: true },
          );
        }
        this.injectProperties({
          _DesignId: designId,
          Message: hasMessage ? body.message : null,
          Font: hasMessage ? (cfg.fonts.find((f) => f.key === body.fontKey) || {}).name : null,
          Colour: hasMessage ? (cfg.colours.find((c) => c.key === body.colourKey) || {}).name : null,
          Photo: body.photos.length ? "provided" : null,
          _MessagePlacement: body.messageLayout
            ? "overlay " + body.messageLayout.anchor.x.toFixed(2) + "," + body.messageLayout.anchor.y.toFixed(2)
            : null,
          // v3.0: absent = landscape. Unlike _MessagePlacement the backend
          // READS this one (design-missing placeholders reconstruct from it).
          _Orientation: body.orientation === "portrait" ? "portrait" : null,
        });
        this.designReady = true;
        this.state.saving = false;
        this.refreshValidity();
        this.form.requestSubmit();
      } catch (err) {
        console.warn("[customiser] design save failed", err);
        this.designReady = false;
        this.state.saving = false;
        this.refreshValidity();
        this.errorSlot.appendChild(
          this.buildError(err.display ? err.message : "Could not save your design. Please try again."),
        );
      }
    }

    injectProperties(props) {
      this.form.querySelectorAll("input[data-customiser-property]").forEach((el) => el.remove());
      Object.entries(props).forEach(([name, value]) => {
        if (value == null) return;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${name}]`;
        input.value = value;
        input.dataset.customiserProperty = "1";
        this.form.appendChild(input);
      });
    }
  }

  if (!customElements.get("tfk-cake-customiser")) {
    customElements.define("tfk-cake-customiser", TfkCakeCustomiser);
  }
  // The class is defined after the first shim above, so it joins the export
  // here. node:test drives its methods on an instance built without a DOM.
  if (typeof module !== "undefined" && module.exports) {
    module.exports.TfkCakeCustomiser = TfkCakeCustomiser;
  }

  /* Contract v2.8: the app proxy's GET /apps/customise/complete renders the
     mount node; presence of #cake-customiser-complete IS the mode switch.
     data-contract gates ONLY this mount (>= 2.8): the fragment hardcodes
     "2.8" (2026-08-04 UAT finding), so the v2.9/v3.0 feature gates live on
     the config contract inside loadConfig instead. parseFloat is safe: the
     app team has committed the version after "2.9" is "3.0", never "2.10"
     (storefront-api.md, v2.9 section). */
  function mountCompleteMode() {
    const mount = document.getElementById("cake-customiser-complete");
    if (!mount) return;
    if (!(parseFloat(mount.dataset.contract) >= 2.8)) return;
    const el = document.createElement("tfk-cake-customiser");
    el.className = "tfk-customiser";
    el.dataset.mode = "complete";
    el.dataset.token = mount.dataset.token || "";
    el.dataset.contract = mount.dataset.contract || "";
    // Contract v3.4: the app marks the designer the customer reached through
    // "Change this cake's design" on the cake's own page.
    if (mount.dataset.change === "1") el.dataset.change = "1";
    mount.textContent = "";
    mount.appendChild(el);
  }
  // Guarded so the file can be require()d for its geometry in node:test, where
  // there is no document. In a browser this is the same branch as before.
  if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", mountCompleteMode);
    } else {
      mountCompleteMode();
    }
  }
})();
